import type { Language } from "../../../types/i18n";

// Part of the healthMore module (see ../healthMore.ts). English is the
// reference; every other language must carry the same keys.
//
// Prefixes: cardx (HealthPass card, wallet, sharing, MED ACCESS), emergx
// (emergency profile, devices, audit log, emergency PDF), fhirx (FHIR pages
// and fhirUtils display text).
const en = {
  cardxQrAlt: "QR code",
  cardxBookingCodeRequired: "Please enter a booking code.",
  cardxQrGenerateFailed: "Failed to generate the QR code. Please try again.",
  cardxCheckinQrTitle: "Check-in QR",
  cardxValidOneHourUntil: "Valid for 1 hour (until {time})",
  cardxCheckinScanHint:
    "Let the travel provider scan this QR code to start check-in.",
  cardxCheckinTitle: "Check-in",
  cardxCheckinSubtitle: "Generate a QR code for your travel provider",
  cardxBookingCode: "Booking code",
  cardxBookingCodePlaceholder: "e.g. ABC123XY",
  cardxProviderName: "Provider name",
  cardxOptionalParen: "(optional)",
  cardxProviderPlaceholder: "e.g. MSC Cruises",
  cardxGenerateQr: "Generate QR",
  cardxEmergencyQrFailed: "Failed to generate the emergency QR code.",
  cardxEmergencyQrGenerating: "Generating emergency QR…",
  cardxRetry: "Retry",
  cardxEmergencyQrTitle: "Emergency QR",
  cardxEmergencyQrSubtitle:
    "First responders can scan this for immediate access",
  cardxEmergencyNoLogin: "No login required for first responders",
  cardxEmergencyAuditNote:
    "Name, email, and role are recorded. You can review the access history later.",
  cardxMedAccessFailed: "Failed to generate the MED ACCESS token.",
  cardxMedAccessQrTitle: "MED ACCESS QR",
  cardxValidHoursUntil: "Valid for {hours}h (until {time})",
  cardxDoctorMustBeRegistered: "The doctor must be registered on HEALTHPASS",
  cardxConsentNotificationNote:
    "You'll receive a consent notification before any data is shared.",
  cardxMedAccessTitle: "Medical access",
  cardxMedAccessSubtitle: "Allow a doctor to access your full health profile",
  cardxAccessDuration: "Access duration",
  cardxDurationHours: "{n}h",
  cardxDurationDays: "{n}d",
  cardxAccessExpiresNote:
    "Access expires automatically. You can revoke it at any time.",
  cardxDoctorAccountNote:
    "The doctor must have a HEALTHPASS account. You'll confirm access via a notification before any data is visible.",
  cardxActionCheckin: "Check-in",
  cardxActionCheckinHint: "QR for travel provider",
  cardxActionEmergency: "Emergency",
  cardxActionEmergencyHint: "First responder access",
  cardxActionMedAccess: "Med access",
  cardxActionMedAccessHint: "Share with your doctor",
  cardxActionWallet: "Wallet",
  cardxActionWalletHint: "Open your HealthPass wallet",
  cardxEntryOne: "{n} entry",
  cardxEntryMany: "{n} entries",
  cardxShortEmergency: "Emergency",
  cardxShortMedication: "Medication",
  cardxShortVaccinations: "Vaccinations",
  cardxShortCheckin: "Check-in docs",
  cardxShortAppointments: "Appointments",
  cardxShortInsurance: "Insurance",
  cardxShortTravelDocs: "Travel docs",
  cardxShortLabResults: "Lab results",
  cardxStatusAdd: "Add",
  cardxStatusComplete: "Complete",
  cardxStatusPartial: "Partial",
  cardxBackAria: "HealthPass card, wallet side",
  cardxWalletHeading: "WALLET",
  cardxPassesFilled: "{filled} of {total} passes filled",
  cardxGenderNotStated: "gender not stated",
  cardxFrontAria: "HealthPass card of {name}",
  cardxCardQrAlt: "Card QR code",
  cardxQrLoading: "QR code loading",
  cardxCardNumber: "CARD NUMBER",
  cardxValidUntil: "VALID UNTIL",
  cardxStatusGranted: "Granted",
  cardxStatusPending: "Pending",
  cardxStatusDenied: "Denied",
  cardxStatusRevoked: "Revoked",
  cardxSharedData: "Shared data",
  cardxNoSharedData: "No shared data yet",
  cardxUnknownDoctor: "Unknown doctor",
  cardxMedAccessLabel: "Med access",
  cardxNoDataYet: "No data yet",
  cardxRevokeShareConfirm:
    "Revoke this share link? Anyone with the link will lose access immediately.",
  cardxRevokeShareFailed: "Failed to revoke the link",
  cardxExpiredRevoked: "Expired / revoked",
  cardxExpiresOn: "Expires {date}",
  cardxCopyLink: "Copy link",
  cardxOpenLink: "Open link",
  cardxCreateShareFailed: "Failed to create the share link",
  cardxSharePasswordPlaceholder: "Leave empty for no password",
  cardxClaimNameEmailRequired: "Please enter your name and email.",
  cardxClaimEmailInvalid: "Please enter a valid email address.",
  cardxClaimFailed: "Failed to send the access request.",
  cardxMedRequestTitle: "Medical access request",
  cardxMedRequestSubtitle: "HEALTHPASS secure health record",
  cardxVerifyingToken: "Verifying access token…",
  cardxMedInfoBanner:
    "A patient has shared a time-limited medical access QR code with you.",
  cardxRowValidUntil: "Valid until",
  cardxRowAccessLevel: "Access level",
  cardxRowAccessLevelValue: "Full health record (doctors only)",
  cardxRowConsentRequired: "Consent required",
  cardxRowConsentRequiredValue: "Yes – the patient will be notified",
  cardxNeedAccount:
    "You need a HEALTHPASS account to request access. Log in or register below.",
  cardxLoginToContinue: "Log in to continue",
  cardxRequestAccess: "Request access",
  cardxClaimIntro:
    "Your details will be sent to the patient along with the access request.",
  cardxYourFullName: "Your full name",
  cardxDoctorNamePlaceholder: "Dr. Jane Smith",
  cardxYourEmail: "Your email",
  cardxDoctorEmailPlaceholder: "jane.smith@hospital.com",
  cardxSendAccessRequest: "Send access request",
  cardxWaitingApproval: "Waiting for patient approval",
  cardxWaitingApprovalText:
    "The patient has been notified and must approve your request. This page will update automatically.",
  cardxCheckingEvery5s: "Checking every 5 seconds…",
  cardxAccessGrantedBanner:
    "Access granted. The patient has approved your request.",
  cardxLoadingRecords: "Loading health records…",
  cardxAccessDeniedTitle: "Access denied",
  cardxAccessDeniedText: "The patient has denied this access request.",
  cardxLinkExpiredTitle: "Link expired",
  cardxLinkExpiredText:
    "This access link has expired. Please ask the patient to generate a new one.",
  cardxInvalidLinkTitle: "Invalid link",
  cardxInvalidLinkText:
    "This access link is not valid or has already been used.",
  cardxMedFooter: "HEALTHPASS · Secure medical access · travelhealthpass.app",
  cardxCritHigh: "High",
  cardxCritLow: "Low",
  cardxCritUnableToAssess: "Unable to assess",
  cardxBloodType: "Blood type",
  cardxAllergiesCount: "Allergies ({n})",
  cardxCriticalMedsCount: "Critical medications ({n})",
  cardxExtendedProfile: "Extended health profile",
  cardxReqLoadFailed: "Failed to load access requests.",
  cardxReqUpdateFailed: "Failed to update the request. Please try again.",
  cardxReqTitle: "Medical access requests",
  cardxReqSubtitle: "Doctors who have scanned your MED ACCESS QR code",
  cardxReqEmpty: "No medical access requests yet.",
  cardxReqEmptyHint:
    "When a doctor scans your MED ACCESS QR code, they will appear here.",
  cardxReqSectionAwaitingDoctor: "Shared – awaiting doctor",
  cardxReqSectionNeedsResponse: "Needs your response",
  cardxReqSectionResolved: "Resolved",
  cardxReqAwaitingYou: "Awaiting your response",
  cardxReqWaitingDoctor: "Waiting for doctor",
  cardxReqGranted: "Access granted",
  cardxReqDoctorUnknown: "Doctor not yet identified",
  cardxReqRequested: "Requested: {date}",
  cardxReqExpires: "Expires: {date}",
  cardxReqAccessed: "Accessed: {date}",
  cardxReqGrant: "Grant access",
  cardxReqDeny: "Deny",
  cardxReqGrantedNote:
    "The doctor has access to your health records until the token expires.",
  cardxPdfGenderMale: "M",
  cardxPdfGenderFemale: "F",
  cardxPdfWalletHeading: "HEALTHPASS WALLET",
  emergxPdfCritical: "CRITICAL",
  emergxPdfMild: "Mild",
  emergxPdfNotAssessed: "Not assessed",
  emergxPdfTitle: "EMERGENCY CARD",
  emergxPdfPatientInfo: "Patient information",
  emergxPdfDateOfBirth: "Date of birth",
  emergxPdfAge: "Age",
  emergxPdfGender: "Gender",
  emergxPdfBloodType: "Blood type",
  emergxPdfHeightWeight: "Height / weight",
  emergxPdfNationality: "Nationality",
  emergxPdfAllergies: "Allergies & intolerances",
  emergxPdfNoAllergies: "No allergies documented",
  emergxPdfMedications: "Current medications",
  emergxPdfNoMedications: "No medications documented",
  emergxPdfContacts: "Emergency contacts",
  emergxPdfNoContacts: "No contacts provided",
  emergxPdfQrTitle: "Emergency QR code (first responder access)",
  emergxPdfQrHint: "Scan the QR code for instant access to emergency data",
  emergxPdfGeneratedOn: "Generated on: {date}",
  emergxPdfDisclaimer: "This card does not replace medical treatment.",
  emergxRevoked: "Revoked",
  emergxAuditTitle: "Access audit log",
  emergxAuditEmpty: "No access events recorded yet.",
  emergxAuditAccessType: "Access type:",
  emergxAuditReason: "Reason:",
  emergxAuditEmail: "Email:",
  emergxAuditTokenExpired: "Token expired:",
  emergxAuditRevokedAt: "Revoked:",
  emergxAuditConsent: "Consent documented:",
  emergxYes: "Yes",
  emergxAuditImpliedConsent: "Implied consent",
  emergxAuditResources: "Resources accessed:",
  emergxSevCritical: "Critical",
  emergxSevHigh: "High",
  emergxSevMedium: "Medium",
  emergxSevLow: "Low",
  emergxValidating: "Validating…",
  emergxNoValidationData: "No validation data.",
  emergxValidationTitle: "Profile validation",
  emergxRevalidate: "Re-validate",
  emergxCompleteness: "Completeness",
  emergxNoCriticalIssues: "No critical issues found",
  emergxAllChecksPassed: "All checks passed",
  emergxFixIn: "Fix in {route}",
  emergxLastValidated: "Last validated: {date}",
  emergxNo: "No",
  emergxDevPacemaker: "Pacemaker",
  emergxDevIcd: "Implantable defibrillator (ICD)",
  emergxDevInsulinPump: "Insulin pump",
  emergxDevCochlear: "Cochlear implant",
  emergxDevStent: "Stent",
  emergxDevJoint: "Artificial joint",
  emergxDevHeartValve: "Heart valve",
  emergxDevNeurostimulator: "Neurostimulator",
  emergxDevPort: "Port / catheter",
  emergxDevLoadFailed: "Could not load your medical devices.",
  emergxDevSaveFailed: "Could not save the device. Please try again.",
  emergxDevRemoveFailed: "Could not remove the device.",
  emergxDevTitle: "Medical devices",
  emergxDevAdd: "Add device",
  emergxDevEmpty: "No medical devices recorded",
  emergxDevImplantedOn: "implanted {date}",
  emergxDevMriSafe: "MRI safe",
  emergxDevMriUnsafe: "NOT MRI safe",
  emergxDevRemove: "Remove {type}",
  emergxDevType: "Device type",
  emergxDevManufacturer: "Manufacturer",
  emergxDevModel: "Model",
  emergxDevImplantDate: "Implanted on",
  emergxDevMriLabel: "Safe in an MRI scanner",
  emergxDevNotRecorded: "Not recorded",
  emergxDevNotes: "Notes",
  emergxDevNotesPlaceholder: "Anything a clinician should know",
  emergxDevSave: "Save device",
  emergxCodeHigh: "High",
  emergxCodeLow: "Low",
  emergxCodeUnableToAssess: "Unable to assess",
  emergxCodeMild: "Mild",
  emergxCodeModerate: "Moderate",
  emergxCodeSevere: "Severe",
  emergxCodeActive: "Active",
  emergxCodeInactive: "Inactive",
  emergxCodeResolved: "Resolved",
  emergxCodeRemission: "In remission",
  emergxCodeRecurrence: "Recurrence",
  emergxCodeRelapse: "Relapse",
  emergxConsentCheckboxAria: "I consent to emergency data access",
  emergxAgeYears: "{n} years",
  emergxNotAvailable: "{title} not available",
  fhirxDashboardError: "FHIR dashboard error",
  fhirxLoadError: "Failed to load FHIR resources",
  fhirxTryAgain: "Try again",
  fhirxDashboardTitle: "FHIR dashboard",
  fhirxDashboardSubtitle: "Overview of your health data in FHIR format",
  fhirxTotalResources: "Total resources",
  fhirxAllResources: "All FHIR resources",
  fhirxObservations: "Observations",
  fhirxObservationsHint: "Medical measurements",
  fhirxAllergies: "Allergies",
  fhirxAllergiesHint: "Known allergies",
  fhirxConditions: "Diagnoses",
  fhirxConditionsHint: "Medical diagnoses",
  fhirxImmunizations: "Vaccinations",
  fhirxImmunizationsHint: "Completed vaccinations",
  fhirxMedications: "Medications",
  fhirxMedicationsHint: "Prescriptions",
  fhirxLoadingResources: "Loading FHIR resources…",
  fhirxNoResources: "No FHIR resources found",
  fhirxNoResourcesHint: "No health data in FHIR format is available yet.",
  fhirxExtTitle: "Extended FHIR resources",
  fhirxExtSubtitle:
    "Dashboard for testing the extended FHIR resource types: DiagnosticReport, MedicationRequest, Encounter, Procedure, RelatedPerson, Flag",
  fhirxRefreshAll: "Refresh all",
  fhirxExportAll: "Export all resources",
  fhirxExtLoading: "Loading extended FHIR resources…",
  fhirxExtLoaded: "Successfully loaded {n} extended FHIR resources",
  fhirxExtAllResources: "All extended FHIR resources",
  fhirxDiagnosticReports: "Diagnostic reports",
  fhirxDiagnosticReportsHint: "Lab & radiology reports",
  fhirxMedicationRequests: "Medication requests",
  fhirxMedicationRequestsHint: "Prescribed medications",
  fhirxEncounters: "Encounters",
  fhirxEncountersHint: "Medical visits",
  fhirxProcedures: "Procedures",
  fhirxProceduresHint: "Medical procedures",
  fhirxRelatedPersons: "Related persons",
  fhirxRelatedPersonsHint: "Emergency contacts",
  fhirxHealthFlags: "Health flags",
  fhirxFlagsHint: "Warnings & alerts",
  fhirxTestStatus: "Test status",
  fhirxAllTypesImplemented: "All types implemented",
  fhirxImplementationStatus: "Implementation status",
  fhirxImplemented: "Implemented",
  fhirxExportOptions: "Export options",
  fhirxExportIntro:
    "Export all extended FHIR resources as a complete bundle, including:",
  fhirxExportItemReports: "Diagnostic reports with lab results",
  fhirxExportItemMeds: "Medication requests with dosing",
  fhirxExportItemEncounters: "Medical encounters and visits",
  fhirxExportItemProcedures: "Performed procedures",
  fhirxExportItemRelated: "Related persons and contacts",
  fhirxExportItemFlags: "Health flags and warnings",
  fhirxDownloadBundle: "Download complete bundle",
  fhirxDebugInfo: "Debug information",
  fhirxBundleTotals: "Bundle totals:",
  fhirxAdditionalResources: "Additional resources:",
  fhirxLastUpdated: "Last updated: {date}",
  fhirxTypePatient: "Patients",
  fhirxTypeObservation: "Measurements",
  fhirxTypeAllergyIntolerance: "Allergies",
  fhirxTypeCondition: "Diagnoses",
  fhirxTypeImmunization: "Immunizations",
  fhirxTypeMedicationStatement: "Medications",
  fhirxTypeDiagnosticReport: "Diagnostic reports",
  fhirxTypeMedicationRequest: "Prescriptions",
  fhirxTypeEncounter: "Doctor visits",
  fhirxTypeProcedure: "Procedures",
  fhirxTypeDocumentReference: "Documents",
  fhirxTypeMedia: "Media",
  fhirxTypeRelatedPerson: "Contact persons",
  fhirxTypePractitioner: "Doctors",
  fhirxTypeCareTeam: "Care team",
  fhirxTypeCoverage: "Insurance",
  fhirxTypeClaim: "Claims",
  fhirxTypeConsent: "Consents",
  fhirxTypeFlag: "Warnings",
  fhirxTypeGoal: "Goals",
  fhirxTypeRiskAssessment: "Risk assessments",
  fhirxTypeServiceRequest: "Service requests",
  fhirxTypeAppointment: "Appointments",
  fhirxTypeLocation: "Locations",
  fhirxTypeOrganization: "Organizations",
  fhirxTypeBundle: "Bundles",
  fhirxUnknownPatient: "Unknown patient",
  fhirxBornOn: "Born {date}",
  fhirxActive: "Active",
  fhirxInactive: "Inactive",
  fhirxYes: "Yes",
  fhirxNo: "No",
  fhirxMeasurement: "Measurement",
  fhirxAllergy: "Allergy",
  fhirxCatFood: "Food",
  fhirxCatMedication: "Medication",
  fhirxCatEnvironment: "Environment",
  fhirxCatBiologic: "Biologic",
  fhirxDiagnosis: "Diagnosis",
  fhirxDoseNumber: "Dose {n}",
  fhirxVaccination: "Vaccination",
  fhirxBundleGenerated: "FHIR bundle generated successfully",
  fhirxBundleFailed: "The FHIR bundle could not be generated",
  fhirxExported: "FHIR data exported successfully",
  fhirxExportFailed: "FHIR data could not be exported",
  fhirxInteropSubtitle: "HL7 FHIR R5 interoperability for health data",
  fhirxExport: "Export",
  fhirxGenerateBundle: "Generate bundle",
  fhirxServiceStatus: "FHIR service status",
  fhirxVersion: "Version: {version}",
  fhirxSearchPlaceholder: "Search resources…",
  fhirxAllTypes: "All types",
  fhirxResourcesCount: "FHIR resources ({n})",
  fhirxAllData: "All your FHIR health data",
  fhirxFilteredBy: "FHIR resources filtered by type: {type}",
  fhirxNoSearchMatch: "No resources match your search",
  fhirxNoResourcesShort: "No resources found",
  fhirxResourceDetails: "FHIR resource details",
  fhirxCategory: "Category",
  fhirxSeverity: "Severity",
};

type Strings = typeof en;

const de: Strings = {
  cardxQrAlt: "QR-Code",
  cardxBookingCodeRequired: "Bitte geben Sie einen Buchungscode ein.",
  cardxQrGenerateFailed:
    "Der QR-Code konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
  cardxCheckinQrTitle: "Check-in-QR",
  cardxValidOneHourUntil: "1 Stunde gültig (bis {time})",
  cardxCheckinScanHint:
    "Lassen Sie den Reiseanbieter diesen QR-Code scannen, um den Check-in zu starten.",
  cardxCheckinTitle: "Check-in",
  cardxCheckinSubtitle: "QR-Code für Ihren Reiseanbieter erstellen",
  cardxBookingCode: "Buchungscode",
  cardxBookingCodePlaceholder: "z. B. ABC123XY",
  cardxProviderName: "Name des Anbieters",
  cardxOptionalParen: "(optional)",
  cardxProviderPlaceholder: "z. B. MSC Kreuzfahrten",
  cardxGenerateQr: "QR erstellen",
  cardxEmergencyQrFailed: "Der Notfall-QR-Code konnte nicht erstellt werden.",
  cardxEmergencyQrGenerating: "Notfall-QR wird erstellt…",
  cardxRetry: "Erneut versuchen",
  cardxEmergencyQrTitle: "Notfall-QR",
  cardxEmergencyQrSubtitle:
    "Ersthelfer können ihn für sofortigen Zugriff scannen",
  cardxEmergencyNoLogin: "Für Ersthelfer ist keine Anmeldung nötig",
  cardxEmergencyAuditNote:
    "Name, E-Mail und Rolle werden protokolliert. Den Zugriffsverlauf können Sie später einsehen.",
  cardxMedAccessFailed: "Der MED-ACCESS-Token konnte nicht erstellt werden.",
  cardxMedAccessQrTitle: "MED-ACCESS-QR",
  cardxValidHoursUntil: "{hours} Std. gültig (bis {time})",
  cardxDoctorMustBeRegistered:
    "Die Ärztin oder der Arzt muss bei HEALTHPASS registriert sein",
  cardxConsentNotificationNote:
    "Sie erhalten eine Einwilligungsanfrage, bevor Daten geteilt werden.",
  cardxMedAccessTitle: "Ärztlicher Zugriff",
  cardxMedAccessSubtitle:
    "Einer Ärztin oder einem Arzt Zugriff auf Ihr vollständiges Gesundheitsprofil geben",
  cardxAccessDuration: "Zugriffsdauer",
  cardxDurationHours: "{n} Std.",
  cardxDurationDays: "{n} T.",
  cardxAccessExpiresNote:
    "Der Zugriff endet automatisch. Sie können ihn jederzeit widerrufen.",
  cardxDoctorAccountNote:
    "Die Ärztin oder der Arzt benötigt ein HEALTHPASS-Konto. Sie bestätigen den Zugriff per Benachrichtigung, bevor Daten sichtbar werden.",
  cardxActionCheckin: "Check-in",
  cardxActionCheckinHint: "QR für Reiseanbieter",
  cardxActionEmergency: "Notfall",
  cardxActionEmergencyHint: "Zugriff für Ersthelfer",
  cardxActionMedAccess: "Arztzugriff",
  cardxActionMedAccessHint: "Mit Ihrer Ärztin / Ihrem Arzt teilen",
  cardxActionWallet: "Wallet",
  cardxActionWalletHint: "Ihre HealthPass-Wallet öffnen",
  cardxEntryOne: "{n} Eintrag",
  cardxEntryMany: "{n} Einträge",
  cardxShortEmergency: "Notfall",
  cardxShortMedication: "Medikation",
  cardxShortVaccinations: "Impfungen",
  cardxShortCheckin: "Check-in-Dok.",
  cardxShortAppointments: "Termine",
  cardxShortInsurance: "Versicherung",
  cardxShortTravelDocs: "Reisedok.",
  cardxShortLabResults: "Laborbefunde",
  cardxStatusAdd: "Hinzufügen",
  cardxStatusComplete: "Vollständig",
  cardxStatusPartial: "Teilweise",
  cardxBackAria: "HealthPass-Karte, Wallet-Seite",
  cardxWalletHeading: "WALLET",
  cardxPassesFilled: "{filled} von {total} Pässen ausgefüllt",
  cardxGenderNotStated: "Geschlecht nicht angegeben",
  cardxFrontAria: "HealthPass-Karte von {name}",
  cardxCardQrAlt: "QR-Code der Karte",
  cardxQrLoading: "QR-Code wird geladen",
  cardxCardNumber: "KARTENNUMMER",
  cardxValidUntil: "GÜLTIG BIS",
  cardxStatusGranted: "Erteilt",
  cardxStatusPending: "Ausstehend",
  cardxStatusDenied: "Abgelehnt",
  cardxStatusRevoked: "Widerrufen",
  cardxSharedData: "Geteilte Daten",
  cardxNoSharedData: "Noch keine geteilten Daten",
  cardxUnknownDoctor: "Unbekannte Ärztin / unbekannter Arzt",
  cardxMedAccessLabel: "Arztzugriff",
  cardxNoDataYet: "Noch keine Daten",
  cardxRevokeShareConfirm:
    "Diesen Freigabelink widerrufen? Alle mit dem Link verlieren sofort den Zugriff.",
  cardxRevokeShareFailed: "Der Link konnte nicht widerrufen werden",
  cardxExpiredRevoked: "Abgelaufen / widerrufen",
  cardxExpiresOn: "Läuft ab am {date}",
  cardxCopyLink: "Link kopieren",
  cardxOpenLink: "Link öffnen",
  cardxCreateShareFailed: "Der Freigabelink konnte nicht erstellt werden",
  cardxSharePasswordPlaceholder: "Leer lassen für kein Passwort",
  cardxClaimNameEmailRequired:
    "Bitte geben Sie Ihren Namen und Ihre E-Mail-Adresse ein.",
  cardxClaimEmailInvalid: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
  cardxClaimFailed: "Die Zugriffsanfrage konnte nicht gesendet werden.",
  cardxMedRequestTitle: "Anfrage auf ärztlichen Zugriff",
  cardxMedRequestSubtitle: "Sichere HEALTHPASS-Gesundheitsakte",
  cardxVerifyingToken: "Zugriffstoken wird geprüft…",
  cardxMedInfoBanner:
    "Eine Patientin oder ein Patient hat einen zeitlich begrenzten QR-Code für ärztlichen Zugriff mit Ihnen geteilt.",
  cardxRowValidUntil: "Gültig bis",
  cardxRowAccessLevel: "Zugriffsumfang",
  cardxRowAccessLevelValue: "Vollständige Gesundheitsakte (nur Ärzte)",
  cardxRowConsentRequired: "Einwilligung erforderlich",
  cardxRowConsentRequiredValue:
    "Ja – die Patientin / der Patient wird benachrichtigt",
  cardxNeedAccount:
    "Für eine Zugriffsanfrage benötigen Sie ein HEALTHPASS-Konto. Melden Sie sich unten an oder registrieren Sie sich.",
  cardxLoginToContinue: "Zum Fortfahren anmelden",
  cardxRequestAccess: "Zugriff anfragen",
  cardxClaimIntro:
    "Ihre Angaben werden zusammen mit der Zugriffsanfrage an die Patientin / den Patienten gesendet.",
  cardxYourFullName: "Ihr vollständiger Name",
  cardxDoctorNamePlaceholder: "Dr. Anna Schmidt",
  cardxYourEmail: "Ihre E-Mail-Adresse",
  cardxDoctorEmailPlaceholder: "anna.schmidt@klinik.de",
  cardxSendAccessRequest: "Zugriffsanfrage senden",
  cardxWaitingApproval:
    "Warten auf Freigabe durch die Patientin / den Patienten",
  cardxWaitingApprovalText:
    "Die Patientin / der Patient wurde benachrichtigt und muss Ihre Anfrage freigeben. Diese Seite aktualisiert sich automatisch.",
  cardxCheckingEvery5s: "Prüfung alle 5 Sekunden…",
  cardxAccessGrantedBanner:
    "Zugriff erteilt. Die Patientin / der Patient hat Ihre Anfrage freigegeben.",
  cardxLoadingRecords: "Gesundheitsdaten werden geladen…",
  cardxAccessDeniedTitle: "Zugriff abgelehnt",
  cardxAccessDeniedText:
    "Die Patientin / der Patient hat diese Zugriffsanfrage abgelehnt.",
  cardxLinkExpiredTitle: "Link abgelaufen",
  cardxLinkExpiredText:
    "Dieser Zugriffslink ist abgelaufen. Bitten Sie die Patientin / den Patienten, einen neuen zu erstellen.",
  cardxInvalidLinkTitle: "Ungültiger Link",
  cardxInvalidLinkText:
    "Dieser Zugriffslink ist ungültig oder wurde bereits verwendet.",
  cardxMedFooter:
    "HEALTHPASS · Sicherer ärztlicher Zugriff · travelhealthpass.app",
  cardxCritHigh: "Hoch",
  cardxCritLow: "Niedrig",
  cardxCritUnableToAssess: "Nicht beurteilbar",
  cardxBloodType: "Blutgruppe",
  cardxAllergiesCount: "Allergien ({n})",
  cardxCriticalMedsCount: "Kritische Medikamente ({n})",
  cardxExtendedProfile: "Erweitertes Gesundheitsprofil",
  cardxReqLoadFailed: "Zugriffsanfragen konnten nicht geladen werden.",
  cardxReqUpdateFailed:
    "Die Anfrage konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.",
  cardxReqTitle: "Anfragen auf ärztlichen Zugriff",
  cardxReqSubtitle:
    "Ärztinnen und Ärzte, die Ihren MED-ACCESS-QR-Code gescannt haben",
  cardxReqEmpty: "Noch keine Anfragen auf ärztlichen Zugriff.",
  cardxReqEmptyHint:
    "Sobald eine Ärztin oder ein Arzt Ihren MED-ACCESS-QR-Code scannt, erscheint die Anfrage hier.",
  cardxReqSectionAwaitingDoctor: "Geteilt – wartet auf Ärztin / Arzt",
  cardxReqSectionNeedsResponse: "Ihre Antwort ist nötig",
  cardxReqSectionResolved: "Erledigt",
  cardxReqAwaitingYou: "Wartet auf Ihre Antwort",
  cardxReqWaitingDoctor: "Wartet auf Ärztin / Arzt",
  cardxReqGranted: "Zugriff erteilt",
  cardxReqDoctorUnknown: "Ärztin / Arzt noch nicht bekannt",
  cardxReqRequested: "Angefragt: {date}",
  cardxReqExpires: "Läuft ab: {date}",
  cardxReqAccessed: "Abgerufen: {date}",
  cardxReqGrant: "Zugriff erteilen",
  cardxReqDeny: "Ablehnen",
  cardxReqGrantedNote:
    "Die Ärztin / der Arzt hat bis zum Ablauf des Tokens Zugriff auf Ihre Gesundheitsdaten.",
  cardxPdfGenderMale: "M",
  cardxPdfGenderFemale: "W",
  cardxPdfWalletHeading: "HEALTHPASS-WALLET",
  emergxPdfCritical: "KRITISCH",
  emergxPdfMild: "Leicht",
  emergxPdfNotAssessed: "Nicht beurteilt",
  emergxPdfTitle: "NOTFALLAUSWEIS",
  emergxPdfPatientInfo: "Patientendaten",
  emergxPdfDateOfBirth: "Geburtsdatum",
  emergxPdfAge: "Alter",
  emergxPdfGender: "Geschlecht",
  emergxPdfBloodType: "Blutgruppe",
  emergxPdfHeightWeight: "Größe / Gewicht",
  emergxPdfNationality: "Nationalität",
  emergxPdfAllergies: "Allergien & Unverträglichkeiten",
  emergxPdfNoAllergies: "Keine Allergien dokumentiert",
  emergxPdfMedications: "Aktuelle Medikamente",
  emergxPdfNoMedications: "Keine Medikamente dokumentiert",
  emergxPdfContacts: "Notfallkontakte",
  emergxPdfNoContacts: "Keine Kontakte hinterlegt",
  emergxPdfQrTitle: "Notfall-QR-Code (Online-Zugriff für Ersthelfer)",
  emergxPdfQrHint: "QR-Code scannen für sofortigen Zugriff auf Notfalldaten",
  emergxPdfGeneratedOn: "Erstellt am: {date}",
  emergxPdfDisclaimer: "Diese Karte ersetzt keine ärztliche Behandlung.",
  emergxRevoked: "Widerrufen",
  emergxAuditTitle: "Zugriffsprotokoll",
  emergxAuditEmpty: "Noch keine Zugriffe protokolliert.",
  emergxAuditAccessType: "Zugriffsart:",
  emergxAuditReason: "Grund:",
  emergxAuditEmail: "E-Mail:",
  emergxAuditTokenExpired: "Token abgelaufen:",
  emergxAuditRevokedAt: "Widerrufen:",
  emergxAuditConsent: "Einwilligung dokumentiert:",
  emergxYes: "Ja",
  emergxAuditImpliedConsent: "Mutmaßliche Einwilligung",
  emergxAuditResources: "Abgerufene Daten:",
  emergxSevCritical: "Kritisch",
  emergxSevHigh: "Hoch",
  emergxSevMedium: "Mittel",
  emergxSevLow: "Niedrig",
  emergxValidating: "Wird geprüft…",
  emergxNoValidationData: "Keine Prüfdaten.",
  emergxValidationTitle: "Profilprüfung",
  emergxRevalidate: "Erneut prüfen",
  emergxCompleteness: "Vollständigkeit",
  emergxNoCriticalIssues: "Keine kritischen Probleme gefunden",
  emergxAllChecksPassed: "Alle Prüfungen bestanden",
  emergxFixIn: "Beheben unter {route}",
  emergxLastValidated: "Zuletzt geprüft: {date}",
  emergxNo: "Nein",
  emergxDevPacemaker: "Herzschrittmacher",
  emergxDevIcd: "Implantierbarer Defibrillator (ICD)",
  emergxDevInsulinPump: "Insulinpumpe",
  emergxDevCochlear: "Cochlea-Implantat",
  emergxDevStent: "Stent",
  emergxDevJoint: "Künstliches Gelenk",
  emergxDevHeartValve: "Herzklappe",
  emergxDevNeurostimulator: "Neurostimulator",
  emergxDevPort: "Port / Katheter",
  emergxDevLoadFailed: "Ihre Medizinprodukte konnten nicht geladen werden.",
  emergxDevSaveFailed:
    "Das Gerät konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
  emergxDevRemoveFailed: "Das Gerät konnte nicht entfernt werden.",
  emergxDevTitle: "Medizinprodukte",
  emergxDevAdd: "Gerät hinzufügen",
  emergxDevEmpty: "Keine Medizinprodukte erfasst",
  emergxDevImplantedOn: "implantiert am {date}",
  emergxDevMriSafe: "MRT-sicher",
  emergxDevMriUnsafe: "NICHT MRT-sicher",
  emergxDevRemove: "{type} entfernen",
  emergxDevType: "Geräteart",
  emergxDevManufacturer: "Hersteller",
  emergxDevModel: "Modell",
  emergxDevImplantDate: "Implantiert am",
  emergxDevMriLabel: "Sicher im MRT",
  emergxDevNotRecorded: "Nicht erfasst",
  emergxDevNotes: "Notizen",
  emergxDevNotesPlaceholder: "Was medizinisches Personal wissen sollte",
  emergxDevSave: "Gerät speichern",
  emergxCodeHigh: "Hoch",
  emergxCodeLow: "Niedrig",
  emergxCodeUnableToAssess: "Nicht beurteilbar",
  emergxCodeMild: "Leicht",
  emergxCodeModerate: "Mittel",
  emergxCodeSevere: "Schwer",
  emergxCodeActive: "Aktiv",
  emergxCodeInactive: "Inaktiv",
  emergxCodeResolved: "Abgeheilt",
  emergxCodeRemission: "In Remission",
  emergxCodeRecurrence: "Rezidiv",
  emergxCodeRelapse: "Rückfall",
  emergxConsentCheckboxAria:
    "Ich willige in den Notfallzugriff auf die Daten ein",
  emergxAgeYears: "{n} Jahre",
  emergxNotAvailable: "{title} nicht verfügbar",
  fhirxDashboardError: "Fehler im FHIR-Dashboard",
  fhirxLoadError: "Fehler beim Laden der FHIR-Ressourcen",
  fhirxTryAgain: "Erneut versuchen",
  fhirxDashboardTitle: "FHIR-Dashboard",
  fhirxDashboardSubtitle: "Übersicht Ihrer Gesundheitsdaten im FHIR-Format",
  fhirxTotalResources: "Ressourcen gesamt",
  fhirxAllResources: "Alle FHIR-Ressourcen",
  fhirxObservations: "Beobachtungen",
  fhirxObservationsHint: "Medizinische Messungen",
  fhirxAllergies: "Allergien",
  fhirxAllergiesHint: "Bekannte Allergien",
  fhirxConditions: "Diagnosen",
  fhirxConditionsHint: "Medizinische Diagnosen",
  fhirxImmunizations: "Impfungen",
  fhirxImmunizationsHint: "Erfolgte Impfungen",
  fhirxMedications: "Medikamente",
  fhirxMedicationsHint: "Verordnungen",
  fhirxLoadingResources: "FHIR-Ressourcen werden geladen…",
  fhirxNoResources: "Keine FHIR-Ressourcen gefunden",
  fhirxNoResourcesHint:
    "Es liegen noch keine Gesundheitsdaten im FHIR-Format vor.",
  fhirxExtTitle: "Erweiterte FHIR-Ressourcen",
  fhirxExtSubtitle:
    "Dashboard zum Testen der erweiterten FHIR-Ressourcentypen: DiagnosticReport, MedicationRequest, Encounter, Procedure, RelatedPerson, Flag",
  fhirxRefreshAll: "Alle aktualisieren",
  fhirxExportAll: "Alle Ressourcen exportieren",
  fhirxExtLoading: "Erweiterte FHIR-Ressourcen werden geladen…",
  fhirxExtLoaded: "{n} erweiterte FHIR-Ressourcen erfolgreich geladen",
  fhirxExtAllResources: "Alle erweiterten FHIR-Ressourcen",
  fhirxDiagnosticReports: "Diagnostische Berichte",
  fhirxDiagnosticReportsHint: "Labor- & Radiologieberichte",
  fhirxMedicationRequests: "Medikationsverordnungen",
  fhirxMedicationRequestsHint: "Verordnete Medikamente",
  fhirxEncounters: "Arztbesuche",
  fhirxEncountersHint: "Medizinische Besuche",
  fhirxProcedures: "Eingriffe",
  fhirxProceduresHint: "Medizinische Eingriffe",
  fhirxRelatedPersons: "Kontaktpersonen",
  fhirxRelatedPersonsHint: "Notfallkontakte",
  fhirxHealthFlags: "Gesundheitshinweise",
  fhirxFlagsHint: "Warnungen & Hinweise",
  fhirxTestStatus: "Teststatus",
  fhirxAllTypesImplemented: "Alle Typen implementiert",
  fhirxImplementationStatus: "Implementierungsstatus",
  fhirxImplemented: "Implementiert",
  fhirxExportOptions: "Exportoptionen",
  fhirxExportIntro:
    "Alle erweiterten FHIR-Ressourcen als vollständiges Bundle exportieren, einschließlich:",
  fhirxExportItemReports: "Diagnostische Berichte mit Laborwerten",
  fhirxExportItemMeds: "Medikationsverordnungen mit Dosierung",
  fhirxExportItemEncounters: "Arztbesuche und Termine",
  fhirxExportItemProcedures: "Durchgeführte Eingriffe",
  fhirxExportItemRelated: "Kontaktpersonen und Kontakte",
  fhirxExportItemFlags: "Gesundheitshinweise und Warnungen",
  fhirxDownloadBundle: "Vollständiges Bundle herunterladen",
  fhirxDebugInfo: "Debug-Informationen",
  fhirxBundleTotals: "Bundle-Summen:",
  fhirxAdditionalResources: "Weitere Ressourcen:",
  fhirxLastUpdated: "Zuletzt aktualisiert: {date}",
  fhirxTypePatient: "Patienten",
  fhirxTypeObservation: "Messwerte",
  fhirxTypeAllergyIntolerance: "Allergien",
  fhirxTypeCondition: "Diagnosen",
  fhirxTypeImmunization: "Impfungen",
  fhirxTypeMedicationStatement: "Medikamente",
  fhirxTypeDiagnosticReport: "Diagnostische Berichte",
  fhirxTypeMedicationRequest: "Medikationsverordnungen",
  fhirxTypeEncounter: "Arztbesuche",
  fhirxTypeProcedure: "Eingriffe",
  fhirxTypeDocumentReference: "Dokumente",
  fhirxTypeMedia: "Medien",
  fhirxTypeRelatedPerson: "Kontaktpersonen",
  fhirxTypePractitioner: "Ärztinnen und Ärzte",
  fhirxTypeCareTeam: "Behandlungsteam",
  fhirxTypeCoverage: "Versicherung",
  fhirxTypeClaim: "Leistungsabrechnungen",
  fhirxTypeConsent: "Einwilligungen",
  fhirxTypeFlag: "Warnhinweise",
  fhirxTypeGoal: "Ziele",
  fhirxTypeRiskAssessment: "Risikobewertungen",
  fhirxTypeServiceRequest: "Leistungsanforderungen",
  fhirxTypeAppointment: "Termine",
  fhirxTypeLocation: "Standorte",
  fhirxTypeOrganization: "Organisationen",
  fhirxTypeBundle: "Bundles",
  fhirxUnknownPatient: "Unbekannte Patientin / unbekannter Patient",
  fhirxBornOn: "Geb. {date}",
  fhirxActive: "Aktiv",
  fhirxInactive: "Inaktiv",
  fhirxYes: "Ja",
  fhirxNo: "Nein",
  fhirxMeasurement: "Messung",
  fhirxAllergy: "Allergie",
  fhirxCatFood: "Nahrungsmittel",
  fhirxCatMedication: "Medikament",
  fhirxCatEnvironment: "Umwelt",
  fhirxCatBiologic: "Biologisch",
  fhirxDiagnosis: "Diagnose",
  fhirxDoseNumber: "{n}. Dosis",
  fhirxVaccination: "Impfung",
  fhirxBundleGenerated: "FHIR-Bundle erfolgreich erstellt",
  fhirxBundleFailed: "FHIR-Bundle konnte nicht erstellt werden",
  fhirxExported: "FHIR-Daten erfolgreich exportiert",
  fhirxExportFailed: "FHIR-Daten konnten nicht exportiert werden",
  fhirxInteropSubtitle: "HL7-FHIR-R5-Interoperabilität für Gesundheitsdaten",
  fhirxExport: "Exportieren",
  fhirxGenerateBundle: "Bundle erstellen",
  fhirxServiceStatus: "Status des FHIR-Dienstes",
  fhirxVersion: "Version: {version}",
  fhirxSearchPlaceholder: "Ressourcen suchen…",
  fhirxAllTypes: "Alle Typen",
  fhirxResourcesCount: "FHIR-Ressourcen ({n})",
  fhirxAllData: "Alle Ihre FHIR-Gesundheitsdaten",
  fhirxFilteredBy: "FHIR-Ressourcen gefiltert nach Typ: {type}",
  fhirxNoSearchMatch: "Keine Ressourcen entsprechen Ihrer Suche",
  fhirxNoResourcesShort: "Keine Ressourcen gefunden",
  fhirxResourceDetails: "FHIR-Ressourcendetails",
  fhirxCategory: "Kategorie",
  fhirxSeverity: "Schweregrad",
};

const fr: Strings = {
  cardxQrAlt: "Code QR",
  cardxBookingCodeRequired: "Veuillez saisir un code de réservation.",
  cardxQrGenerateFailed:
    "Impossible de générer le code QR. Veuillez réessayer.",
  cardxCheckinQrTitle: "QR d'enregistrement",
  cardxValidOneHourUntil: "Valable 1 heure (jusqu'à {time})",
  cardxCheckinScanHint:
    "Faites scanner ce code QR par le prestataire de voyage pour lancer l'enregistrement.",
  cardxCheckinTitle: "Enregistrement",
  cardxCheckinSubtitle: "Générer un code QR pour votre prestataire de voyage",
  cardxBookingCode: "Code de réservation",
  cardxBookingCodePlaceholder: "p. ex. ABC123XY",
  cardxProviderName: "Nom du prestataire",
  cardxOptionalParen: "(facultatif)",
  cardxProviderPlaceholder: "p. ex. MSC Croisières",
  cardxGenerateQr: "Générer le QR",
  cardxEmergencyQrFailed: "Impossible de générer le code QR d'urgence.",
  cardxEmergencyQrGenerating: "Génération du QR d'urgence…",
  cardxRetry: "Réessayer",
  cardxEmergencyQrTitle: "QR d'urgence",
  cardxEmergencyQrSubtitle:
    "Les secouristes peuvent le scanner pour un accès immédiat",
  cardxEmergencyNoLogin: "Aucune connexion requise pour les secouristes",
  cardxEmergencyAuditNote:
    "Le nom, l'e-mail et le rôle sont enregistrés. Vous pourrez consulter l'historique des accès plus tard.",
  cardxMedAccessFailed: "Impossible de générer le jeton MED ACCESS.",
  cardxMedAccessQrTitle: "QR MED ACCESS",
  cardxValidHoursUntil: "Valable {hours} h (jusqu'au {time})",
  cardxDoctorMustBeRegistered: "Le médecin doit être inscrit sur HEALTHPASS",
  cardxConsentNotificationNote:
    "Vous recevrez une demande de consentement avant tout partage de données.",
  cardxMedAccessTitle: "Accès médical",
  cardxMedAccessSubtitle:
    "Permettre à un médecin d'accéder à votre profil de santé complet",
  cardxAccessDuration: "Durée d'accès",
  cardxDurationHours: "{n} h",
  cardxDurationDays: "{n} j",
  cardxAccessExpiresNote:
    "L'accès expire automatiquement. Vous pouvez le révoquer à tout moment.",
  cardxDoctorAccountNote:
    "Le médecin doit avoir un compte HEALTHPASS. Vous confirmerez l'accès via une notification avant que des données soient visibles.",
  cardxActionCheckin: "Enregistrement",
  cardxActionCheckinHint: "QR pour le prestataire",
  cardxActionEmergency: "Urgence",
  cardxActionEmergencyHint: "Accès secouristes",
  cardxActionMedAccess: "Accès médical",
  cardxActionMedAccessHint: "Partager avec votre médecin",
  cardxActionWallet: "Portefeuille",
  cardxActionWalletHint: "Ouvrir votre portefeuille HealthPass",
  cardxEntryOne: "{n} entrée",
  cardxEntryMany: "{n} entrées",
  cardxShortEmergency: "Urgence",
  cardxShortMedication: "Médication",
  cardxShortVaccinations: "Vaccins",
  cardxShortCheckin: "Docs d'enregistr.",
  cardxShortAppointments: "Rendez-vous",
  cardxShortInsurance: "Assurance",
  cardxShortTravelDocs: "Docs de voyage",
  cardxShortLabResults: "Analyses",
  cardxStatusAdd: "Ajouter",
  cardxStatusComplete: "Complet",
  cardxStatusPartial: "Partiel",
  cardxBackAria: "Carte HealthPass, côté portefeuille",
  cardxWalletHeading: "PORTEFEUILLE",
  cardxPassesFilled: "{filled} sur {total} pass remplis",
  cardxGenderNotStated: "sexe non indiqué",
  cardxFrontAria: "Carte HealthPass de {name}",
  cardxCardQrAlt: "Code QR de la carte",
  cardxQrLoading: "Chargement du code QR",
  cardxCardNumber: "NUMÉRO DE CARTE",
  cardxValidUntil: "VALABLE JUSQU'AU",
  cardxStatusGranted: "Accordé",
  cardxStatusPending: "En attente",
  cardxStatusDenied: "Refusé",
  cardxStatusRevoked: "Révoqué",
  cardxSharedData: "Données partagées",
  cardxNoSharedData: "Aucune donnée partagée pour le moment",
  cardxUnknownDoctor: "Médecin inconnu",
  cardxMedAccessLabel: "Accès médical",
  cardxNoDataYet: "Pas encore de données",
  cardxRevokeShareConfirm:
    "Révoquer ce lien de partage ? Toute personne disposant du lien perdra immédiatement l'accès.",
  cardxRevokeShareFailed: "Impossible de révoquer le lien",
  cardxExpiredRevoked: "Expirés / révoqués",
  cardxExpiresOn: "Expire le {date}",
  cardxCopyLink: "Copier le lien",
  cardxOpenLink: "Ouvrir le lien",
  cardxCreateShareFailed: "Impossible de créer le lien de partage",
  cardxSharePasswordPlaceholder: "Laisser vide pour aucun mot de passe",
  cardxClaimNameEmailRequired: "Veuillez saisir votre nom et votre e-mail.",
  cardxClaimEmailInvalid: "Veuillez saisir une adresse e-mail valide.",
  cardxClaimFailed: "Impossible d'envoyer la demande d'accès.",
  cardxMedRequestTitle: "Demande d'accès médical",
  cardxMedRequestSubtitle: "Dossier de santé sécurisé HEALTHPASS",
  cardxVerifyingToken: "Vérification du jeton d'accès…",
  cardxMedInfoBanner:
    "Un patient a partagé avec vous un code QR d'accès médical à durée limitée.",
  cardxRowValidUntil: "Valable jusqu'au",
  cardxRowAccessLevel: "Niveau d'accès",
  cardxRowAccessLevelValue: "Dossier de santé complet (médecins uniquement)",
  cardxRowConsentRequired: "Consentement requis",
  cardxRowConsentRequiredValue: "Oui – le patient sera averti",
  cardxNeedAccount:
    "Vous avez besoin d'un compte HEALTHPASS pour demander l'accès. Connectez-vous ou inscrivez-vous ci-dessous.",
  cardxLoginToContinue: "Se connecter pour continuer",
  cardxRequestAccess: "Demander l'accès",
  cardxClaimIntro:
    "Vos coordonnées seront envoyées au patient avec la demande d'accès.",
  cardxYourFullName: "Votre nom complet",
  cardxDoctorNamePlaceholder: "Dr Marie Martin",
  cardxYourEmail: "Votre e-mail",
  cardxDoctorEmailPlaceholder: "marie.martin@hopital.fr",
  cardxSendAccessRequest: "Envoyer la demande d'accès",
  cardxWaitingApproval: "En attente de l'approbation du patient",
  cardxWaitingApprovalText:
    "Le patient a été averti et doit approuver votre demande. Cette page se mettra à jour automatiquement.",
  cardxCheckingEvery5s: "Vérification toutes les 5 secondes…",
  cardxAccessGrantedBanner:
    "Accès accordé. Le patient a approuvé votre demande.",
  cardxLoadingRecords: "Chargement du dossier de santé…",
  cardxAccessDeniedTitle: "Accès refusé",
  cardxAccessDeniedText: "Le patient a refusé cette demande d'accès.",
  cardxLinkExpiredTitle: "Lien expiré",
  cardxLinkExpiredText:
    "Ce lien d'accès a expiré. Demandez au patient d'en générer un nouveau.",
  cardxInvalidLinkTitle: "Lien invalide",
  cardxInvalidLinkText:
    "Ce lien d'accès n'est pas valide ou a déjà été utilisé.",
  cardxMedFooter: "HEALTHPASS · Accès médical sécurisé · travelhealthpass.app",
  cardxCritHigh: "Élevée",
  cardxCritLow: "Faible",
  cardxCritUnableToAssess: "Non évaluable",
  cardxBloodType: "Groupe sanguin",
  cardxAllergiesCount: "Allergies ({n})",
  cardxCriticalMedsCount: "Médicaments critiques ({n})",
  cardxExtendedProfile: "Profil de santé étendu",
  cardxReqLoadFailed: "Impossible de charger les demandes d'accès.",
  cardxReqUpdateFailed:
    "Impossible de mettre à jour la demande. Veuillez réessayer.",
  cardxReqTitle: "Demandes d'accès médical",
  cardxReqSubtitle: "Médecins ayant scanné votre code QR MED ACCESS",
  cardxReqEmpty: "Aucune demande d'accès médical pour le moment.",
  cardxReqEmptyHint:
    "Lorsqu'un médecin scanne votre code QR MED ACCESS, il apparaît ici.",
  cardxReqSectionAwaitingDoctor: "Partagé – en attente du médecin",
  cardxReqSectionNeedsResponse: "Votre réponse est requise",
  cardxReqSectionResolved: "Traitées",
  cardxReqAwaitingYou: "En attente de votre réponse",
  cardxReqWaitingDoctor: "En attente du médecin",
  cardxReqGranted: "Accès accordé",
  cardxReqDoctorUnknown: "Médecin pas encore identifié",
  cardxReqRequested: "Demandé : {date}",
  cardxReqExpires: "Expire : {date}",
  cardxReqAccessed: "Consulté : {date}",
  cardxReqGrant: "Accorder l'accès",
  cardxReqDeny: "Refuser",
  cardxReqGrantedNote:
    "Le médecin a accès à votre dossier de santé jusqu'à l'expiration du jeton.",
  cardxPdfGenderMale: "H",
  cardxPdfGenderFemale: "F",
  cardxPdfWalletHeading: "PORTEFEUILLE HEALTHPASS",
  emergxPdfCritical: "CRITIQUE",
  emergxPdfMild: "Légère",
  emergxPdfNotAssessed: "Non évaluée",
  emergxPdfTitle: "CARTE D'URGENCE",
  emergxPdfPatientInfo: "Informations sur le patient",
  emergxPdfDateOfBirth: "Date de naissance",
  emergxPdfAge: "Âge",
  emergxPdfGender: "Sexe",
  emergxPdfBloodType: "Groupe sanguin",
  emergxPdfHeightWeight: "Taille / poids",
  emergxPdfNationality: "Nationalité",
  emergxPdfAllergies: "Allergies et intolérances",
  emergxPdfNoAllergies: "Aucune allergie documentée",
  emergxPdfMedications: "Traitements en cours",
  emergxPdfNoMedications: "Aucun médicament documenté",
  emergxPdfContacts: "Contacts d'urgence",
  emergxPdfNoContacts: "Aucun contact renseigné",
  emergxPdfQrTitle: "Code QR d'urgence (accès secouristes)",
  emergxPdfQrHint:
    "Scannez le code QR pour accéder immédiatement aux données d'urgence",
  emergxPdfGeneratedOn: "Généré le : {date}",
  emergxPdfDisclaimer: "Cette carte ne remplace pas un traitement médical.",
  emergxRevoked: "Révoqué",
  emergxAuditTitle: "Journal des accès",
  emergxAuditEmpty: "Aucun accès enregistré pour le moment.",
  emergxAuditAccessType: "Type d'accès :",
  emergxAuditReason: "Motif :",
  emergxAuditEmail: "E-mail :",
  emergxAuditTokenExpired: "Jeton expiré :",
  emergxAuditRevokedAt: "Révoqué :",
  emergxAuditConsent: "Consentement documenté :",
  emergxYes: "Oui",
  emergxAuditImpliedConsent: "Consentement présumé",
  emergxAuditResources: "Données consultées :",
  emergxSevCritical: "Critique",
  emergxSevHigh: "Élevée",
  emergxSevMedium: "Moyenne",
  emergxSevLow: "Faible",
  emergxValidating: "Validation…",
  emergxNoValidationData: "Aucune donnée de validation.",
  emergxValidationTitle: "Validation du profil",
  emergxRevalidate: "Revalider",
  emergxCompleteness: "Complétude",
  emergxNoCriticalIssues: "Aucun problème critique détecté",
  emergxAllChecksPassed: "Toutes les vérifications sont réussies",
  emergxFixIn: "Corriger dans {route}",
  emergxLastValidated: "Dernière validation : {date}",
  emergxNo: "Non",
  emergxDevPacemaker: "Stimulateur cardiaque",
  emergxDevIcd: "Défibrillateur implantable (DAI)",
  emergxDevInsulinPump: "Pompe à insuline",
  emergxDevCochlear: "Implant cochléaire",
  emergxDevStent: "Stent",
  emergxDevJoint: "Prothèse articulaire",
  emergxDevHeartValve: "Valve cardiaque",
  emergxDevNeurostimulator: "Neurostimulateur",
  emergxDevPort: "Chambre implantable / cathéter",
  emergxDevLoadFailed: "Impossible de charger vos dispositifs médicaux.",
  emergxDevSaveFailed:
    "Impossible d'enregistrer le dispositif. Veuillez réessayer.",
  emergxDevRemoveFailed: "Impossible de supprimer le dispositif.",
  emergxDevTitle: "Dispositifs médicaux",
  emergxDevAdd: "Ajouter un dispositif",
  emergxDevEmpty: "Aucun dispositif médical enregistré",
  emergxDevImplantedOn: "implanté le {date}",
  emergxDevMriSafe: "Compatible IRM",
  emergxDevMriUnsafe: "NON compatible IRM",
  emergxDevRemove: "Supprimer {type}",
  emergxDevType: "Type de dispositif",
  emergxDevManufacturer: "Fabricant",
  emergxDevModel: "Modèle",
  emergxDevImplantDate: "Implanté le",
  emergxDevMriLabel: "Compatible avec l'IRM",
  emergxDevNotRecorded: "Non renseigné",
  emergxDevNotes: "Remarques",
  emergxDevNotesPlaceholder: "Ce qu'un soignant doit savoir",
  emergxDevSave: "Enregistrer le dispositif",
  emergxCodeHigh: "Élevée",
  emergxCodeLow: "Faible",
  emergxCodeUnableToAssess: "Non évaluable",
  emergxCodeMild: "Légère",
  emergxCodeModerate: "Modérée",
  emergxCodeSevere: "Sévère",
  emergxCodeActive: "Active",
  emergxCodeInactive: "Inactive",
  emergxCodeResolved: "Résolue",
  emergxCodeRemission: "En rémission",
  emergxCodeRecurrence: "Récidive",
  emergxCodeRelapse: "Rechute",
  emergxConsentCheckboxAria: "Je consens à l'accès d'urgence aux données",
  emergxAgeYears: "{n} ans",
  emergxNotAvailable: "{title} non disponible",
  fhirxDashboardError: "Erreur du tableau de bord FHIR",
  fhirxLoadError: "Erreur lors du chargement des ressources FHIR",
  fhirxTryAgain: "Réessayer",
  fhirxDashboardTitle: "Tableau de bord FHIR",
  fhirxDashboardSubtitle: "Aperçu de vos données de santé au format FHIR",
  fhirxTotalResources: "Total des ressources",
  fhirxAllResources: "Toutes les ressources FHIR",
  fhirxObservations: "Observations",
  fhirxObservationsHint: "Mesures médicales",
  fhirxAllergies: "Allergies",
  fhirxAllergiesHint: "Allergies connues",
  fhirxConditions: "Diagnostics",
  fhirxConditionsHint: "Diagnostics médicaux",
  fhirxImmunizations: "Vaccinations",
  fhirxImmunizationsHint: "Vaccinations effectuées",
  fhirxMedications: "Médicaments",
  fhirxMedicationsHint: "Prescriptions",
  fhirxLoadingResources: "Chargement des ressources FHIR…",
  fhirxNoResources: "Aucune ressource FHIR trouvée",
  fhirxNoResourcesHint:
    "Aucune donnée de santé au format FHIR n'est encore disponible.",
  fhirxExtTitle: "Ressources FHIR étendues",
  fhirxExtSubtitle:
    "Tableau de bord pour tester les types de ressources FHIR étendus : DiagnosticReport, MedicationRequest, Encounter, Procedure, RelatedPerson, Flag",
  fhirxRefreshAll: "Tout actualiser",
  fhirxExportAll: "Exporter toutes les ressources",
  fhirxExtLoading: "Chargement des ressources FHIR étendues…",
  fhirxExtLoaded: "{n} ressources FHIR étendues chargées avec succès",
  fhirxExtAllResources: "Toutes les ressources FHIR étendues",
  fhirxDiagnosticReports: "Rapports diagnostiques",
  fhirxDiagnosticReportsHint: "Rapports de laboratoire et de radiologie",
  fhirxMedicationRequests: "Prescriptions de médicaments",
  fhirxMedicationRequestsHint: "Médicaments prescrits",
  fhirxEncounters: "Consultations",
  fhirxEncountersHint: "Visites médicales",
  fhirxProcedures: "Actes médicaux",
  fhirxProceduresHint: "Actes médicaux réalisés",
  fhirxRelatedPersons: "Personnes liées",
  fhirxRelatedPersonsHint: "Contacts d'urgence",
  fhirxHealthFlags: "Alertes de santé",
  fhirxFlagsHint: "Avertissements et alertes",
  fhirxTestStatus: "État des tests",
  fhirxAllTypesImplemented: "Tous les types sont implémentés",
  fhirxImplementationStatus: "État de l'implémentation",
  fhirxImplemented: "Implémenté",
  fhirxExportOptions: "Options d'export",
  fhirxExportIntro:
    "Exporter toutes les ressources FHIR étendues dans un bundle complet, comprenant :",
  fhirxExportItemReports: "Rapports diagnostiques avec résultats d'analyses",
  fhirxExportItemMeds: "Prescriptions avec posologie",
  fhirxExportItemEncounters: "Consultations et visites médicales",
  fhirxExportItemProcedures: "Actes réalisés",
  fhirxExportItemRelated: "Personnes liées et contacts",
  fhirxExportItemFlags: "Alertes de santé et avertissements",
  fhirxDownloadBundle: "Télécharger le bundle complet",
  fhirxDebugInfo: "Informations de débogage",
  fhirxBundleTotals: "Totaux du bundle :",
  fhirxAdditionalResources: "Ressources supplémentaires :",
  fhirxLastUpdated: "Dernière mise à jour : {date}",
  fhirxTypePatient: "Patients",
  fhirxTypeObservation: "Mesures",
  fhirxTypeAllergyIntolerance: "Allergies",
  fhirxTypeCondition: "Diagnostics",
  fhirxTypeImmunization: "Vaccinations",
  fhirxTypeMedicationStatement: "Médicaments",
  fhirxTypeDiagnosticReport: "Rapports diagnostiques",
  fhirxTypeMedicationRequest: "Prescriptions",
  fhirxTypeEncounter: "Consultations",
  fhirxTypeProcedure: "Actes médicaux",
  fhirxTypeDocumentReference: "Documents",
  fhirxTypeMedia: "Médias",
  fhirxTypeRelatedPerson: "Personnes de contact",
  fhirxTypePractitioner: "Médecins",
  fhirxTypeCareTeam: "Équipe soignante",
  fhirxTypeCoverage: "Assurance",
  fhirxTypeClaim: "Demandes de remboursement",
  fhirxTypeConsent: "Consentements",
  fhirxTypeFlag: "Alertes",
  fhirxTypeGoal: "Objectifs",
  fhirxTypeRiskAssessment: "Évaluations des risques",
  fhirxTypeServiceRequest: "Demandes de prestation",
  fhirxTypeAppointment: "Rendez-vous",
  fhirxTypeLocation: "Lieux",
  fhirxTypeOrganization: "Organisations",
  fhirxTypeBundle: "Bundles",
  fhirxUnknownPatient: "Patient inconnu",
  fhirxBornOn: "Né(e) le {date}",
  fhirxActive: "Actif",
  fhirxInactive: "Inactif",
  fhirxYes: "Oui",
  fhirxNo: "Non",
  fhirxMeasurement: "Mesure",
  fhirxAllergy: "Allergie",
  fhirxCatFood: "Aliment",
  fhirxCatMedication: "Médicament",
  fhirxCatEnvironment: "Environnement",
  fhirxCatBiologic: "Biologique",
  fhirxDiagnosis: "Diagnostic",
  fhirxDoseNumber: "Dose {n}",
  fhirxVaccination: "Vaccination",
  fhirxBundleGenerated: "Bundle FHIR généré avec succès",
  fhirxBundleFailed: "Impossible de générer le bundle FHIR",
  fhirxExported: "Données FHIR exportées avec succès",
  fhirxExportFailed: "Impossible d'exporter les données FHIR",
  fhirxInteropSubtitle:
    "Interopérabilité HL7 FHIR R5 pour les données de santé",
  fhirxExport: "Exporter",
  fhirxGenerateBundle: "Générer le bundle",
  fhirxServiceStatus: "État du service FHIR",
  fhirxVersion: "Version : {version}",
  fhirxSearchPlaceholder: "Rechercher des ressources…",
  fhirxAllTypes: "Tous les types",
  fhirxResourcesCount: "Ressources FHIR ({n})",
  fhirxAllData: "Toutes vos données de santé FHIR",
  fhirxFilteredBy: "Ressources FHIR filtrées par type : {type}",
  fhirxNoSearchMatch: "Aucune ressource ne correspond à votre recherche",
  fhirxNoResourcesShort: "Aucune ressource trouvée",
  fhirxResourceDetails: "Détails de la ressource FHIR",
  fhirxCategory: "Catégorie",
  fhirxSeverity: "Gravité",
};

const es: Strings = {
  cardxQrAlt: "Código QR",
  cardxBookingCodeRequired: "Introduzca un código de reserva.",
  cardxQrGenerateFailed: "No se pudo generar el código QR. Inténtelo de nuevo.",
  cardxCheckinQrTitle: "QR de check-in",
  cardxValidOneHourUntil: "Válido durante 1 hora (hasta las {time})",
  cardxCheckinScanHint:
    "Deje que el proveedor de viaje escanee este código QR para iniciar el check-in.",
  cardxCheckinTitle: "Check-in",
  cardxCheckinSubtitle: "Generar un código QR para su proveedor de viaje",
  cardxBookingCode: "Código de reserva",
  cardxBookingCodePlaceholder: "p. ej. ABC123XY",
  cardxProviderName: "Nombre del proveedor",
  cardxOptionalParen: "(opcional)",
  cardxProviderPlaceholder: "p. ej. MSC Cruceros",
  cardxGenerateQr: "Generar QR",
  cardxEmergencyQrFailed: "No se pudo generar el código QR de emergencia.",
  cardxEmergencyQrGenerating: "Generando QR de emergencia…",
  cardxRetry: "Reintentar",
  cardxEmergencyQrTitle: "QR de emergencia",
  cardxEmergencyQrSubtitle:
    "Los servicios de emergencia pueden escanearlo para acceder de inmediato",
  cardxEmergencyNoLogin:
    "Los servicios de emergencia no necesitan iniciar sesión",
  cardxEmergencyAuditNote:
    "Se registran el nombre, el correo electrónico y la función. Puede revisar el historial de accesos más tarde.",
  cardxMedAccessFailed: "No se pudo generar el token MED ACCESS.",
  cardxMedAccessQrTitle: "QR MED ACCESS",
  cardxValidHoursUntil: "Válido {hours} h (hasta el {time})",
  cardxDoctorMustBeRegistered: "El médico debe estar registrado en HEALTHPASS",
  cardxConsentNotificationNote:
    "Recibirá una solicitud de consentimiento antes de que se comparta ningún dato.",
  cardxMedAccessTitle: "Acceso médico",
  cardxMedAccessSubtitle:
    "Permitir que un médico acceda a su perfil de salud completo",
  cardxAccessDuration: "Duración del acceso",
  cardxDurationHours: "{n} h",
  cardxDurationDays: "{n} d",
  cardxAccessExpiresNote:
    "El acceso caduca automáticamente. Puede revocarlo en cualquier momento.",
  cardxDoctorAccountNote:
    "El médico debe tener una cuenta de HEALTHPASS. Confirmará el acceso mediante una notificación antes de que se vea ningún dato.",
  cardxActionCheckin: "Check-in",
  cardxActionCheckinHint: "QR para el proveedor",
  cardxActionEmergency: "Emergencia",
  cardxActionEmergencyHint: "Acceso de emergencia",
  cardxActionMedAccess: "Acceso médico",
  cardxActionMedAccessHint: "Compartir con su médico",
  cardxActionWallet: "Cartera",
  cardxActionWalletHint: "Abrir su cartera HealthPass",
  cardxEntryOne: "{n} entrada",
  cardxEntryMany: "{n} entradas",
  cardxShortEmergency: "Emergencia",
  cardxShortMedication: "Medicación",
  cardxShortVaccinations: "Vacunas",
  cardxShortCheckin: "Docs de check-in",
  cardxShortAppointments: "Citas",
  cardxShortInsurance: "Seguro",
  cardxShortTravelDocs: "Docs de viaje",
  cardxShortLabResults: "Análisis",
  cardxStatusAdd: "Añadir",
  cardxStatusComplete: "Completo",
  cardxStatusPartial: "Parcial",
  cardxBackAria: "Tarjeta HealthPass, lado cartera",
  cardxWalletHeading: "CARTERA",
  cardxPassesFilled: "{filled} de {total} pases completados",
  cardxGenderNotStated: "sexo no indicado",
  cardxFrontAria: "Tarjeta HealthPass de {name}",
  cardxCardQrAlt: "Código QR de la tarjeta",
  cardxQrLoading: "Cargando código QR",
  cardxCardNumber: "NÚMERO DE TARJETA",
  cardxValidUntil: "VÁLIDA HASTA",
  cardxStatusGranted: "Concedido",
  cardxStatusPending: "Pendiente",
  cardxStatusDenied: "Denegado",
  cardxStatusRevoked: "Revocado",
  cardxSharedData: "Datos compartidos",
  cardxNoSharedData: "Aún no hay datos compartidos",
  cardxUnknownDoctor: "Médico desconocido",
  cardxMedAccessLabel: "Acceso médico",
  cardxNoDataYet: "Aún no hay datos",
  cardxRevokeShareConfirm:
    "¿Revocar este enlace? Cualquier persona con el enlace perderá el acceso de inmediato.",
  cardxRevokeShareFailed: "No se pudo revocar el enlace",
  cardxExpiredRevoked: "Caducados / revocados",
  cardxExpiresOn: "Caduca el {date}",
  cardxCopyLink: "Copiar enlace",
  cardxOpenLink: "Abrir enlace",
  cardxCreateShareFailed: "No se pudo crear el enlace para compartir",
  cardxSharePasswordPlaceholder: "Déjelo vacío para no usar contraseña",
  cardxClaimNameEmailRequired: "Introduzca su nombre y su correo electrónico.",
  cardxClaimEmailInvalid:
    "Introduzca una dirección de correo electrónico válida.",
  cardxClaimFailed: "No se pudo enviar la solicitud de acceso.",
  cardxMedRequestTitle: "Solicitud de acceso médico",
  cardxMedRequestSubtitle: "Historial de salud seguro de HEALTHPASS",
  cardxVerifyingToken: "Verificando el token de acceso…",
  cardxMedInfoBanner:
    "Un paciente ha compartido con usted un código QR de acceso médico de duración limitada.",
  cardxRowValidUntil: "Válido hasta",
  cardxRowAccessLevel: "Nivel de acceso",
  cardxRowAccessLevelValue: "Historial de salud completo (solo médicos)",
  cardxRowConsentRequired: "Consentimiento requerido",
  cardxRowConsentRequiredValue: "Sí – se notificará al paciente",
  cardxNeedAccount:
    "Necesita una cuenta de HEALTHPASS para solicitar acceso. Inicie sesión o regístrese a continuación.",
  cardxLoginToContinue: "Iniciar sesión para continuar",
  cardxRequestAccess: "Solicitar acceso",
  cardxClaimIntro:
    "Sus datos se enviarán al paciente junto con la solicitud de acceso.",
  cardxYourFullName: "Su nombre completo",
  cardxDoctorNamePlaceholder: "Dra. Ana García",
  cardxYourEmail: "Su correo electrónico",
  cardxDoctorEmailPlaceholder: "ana.garcia@hospital.es",
  cardxSendAccessRequest: "Enviar solicitud de acceso",
  cardxWaitingApproval: "Esperando la aprobación del paciente",
  cardxWaitingApprovalText:
    "Se ha notificado al paciente, que debe aprobar su solicitud. Esta página se actualizará automáticamente.",
  cardxCheckingEvery5s: "Comprobando cada 5 segundos…",
  cardxAccessGrantedBanner:
    "Acceso concedido. El paciente ha aprobado su solicitud.",
  cardxLoadingRecords: "Cargando historial de salud…",
  cardxAccessDeniedTitle: "Acceso denegado",
  cardxAccessDeniedText: "El paciente ha denegado esta solicitud de acceso.",
  cardxLinkExpiredTitle: "Enlace caducado",
  cardxLinkExpiredText:
    "Este enlace de acceso ha caducado. Pida al paciente que genere uno nuevo.",
  cardxInvalidLinkTitle: "Enlace no válido",
  cardxInvalidLinkText:
    "Este enlace de acceso no es válido o ya se ha utilizado.",
  cardxMedFooter: "HEALTHPASS · Acceso médico seguro · travelhealthpass.app",
  cardxCritHigh: "Alta",
  cardxCritLow: "Baja",
  cardxCritUnableToAssess: "No evaluable",
  cardxBloodType: "Grupo sanguíneo",
  cardxAllergiesCount: "Alergias ({n})",
  cardxCriticalMedsCount: "Medicamentos críticos ({n})",
  cardxExtendedProfile: "Perfil de salud ampliado",
  cardxReqLoadFailed: "No se pudieron cargar las solicitudes de acceso.",
  cardxReqUpdateFailed:
    "No se pudo actualizar la solicitud. Inténtelo de nuevo.",
  cardxReqTitle: "Solicitudes de acceso médico",
  cardxReqSubtitle: "Médicos que han escaneado su código QR MED ACCESS",
  cardxReqEmpty: "Aún no hay solicitudes de acceso médico.",
  cardxReqEmptyHint:
    "Cuando un médico escanee su código QR MED ACCESS, aparecerá aquí.",
  cardxReqSectionAwaitingDoctor: "Compartido – esperando al médico",
  cardxReqSectionNeedsResponse: "Requiere su respuesta",
  cardxReqSectionResolved: "Resueltas",
  cardxReqAwaitingYou: "Esperando su respuesta",
  cardxReqWaitingDoctor: "Esperando al médico",
  cardxReqGranted: "Acceso concedido",
  cardxReqDoctorUnknown: "Médico aún no identificado",
  cardxReqRequested: "Solicitado: {date}",
  cardxReqExpires: "Caduca: {date}",
  cardxReqAccessed: "Consultado: {date}",
  cardxReqGrant: "Conceder acceso",
  cardxReqDeny: "Denegar",
  cardxReqGrantedNote:
    "El médico tiene acceso a su historial de salud hasta que caduque el token.",
  cardxPdfGenderMale: "H",
  cardxPdfGenderFemale: "M",
  cardxPdfWalletHeading: "CARTERA HEALTHPASS",
  emergxPdfCritical: "CRÍTICA",
  emergxPdfMild: "Leve",
  emergxPdfNotAssessed: "No evaluada",
  emergxPdfTitle: "TARJETA DE EMERGENCIA",
  emergxPdfPatientInfo: "Datos del paciente",
  emergxPdfDateOfBirth: "Fecha de nacimiento",
  emergxPdfAge: "Edad",
  emergxPdfGender: "Sexo",
  emergxPdfBloodType: "Grupo sanguíneo",
  emergxPdfHeightWeight: "Altura / peso",
  emergxPdfNationality: "Nacionalidad",
  emergxPdfAllergies: "Alergias e intolerancias",
  emergxPdfNoAllergies: "No hay alergias documentadas",
  emergxPdfMedications: "Medicación actual",
  emergxPdfNoMedications: "No hay medicamentos documentados",
  emergxPdfContacts: "Contactos de emergencia",
  emergxPdfNoContacts: "No se han indicado contactos",
  emergxPdfQrTitle:
    "Código QR de emergencia (acceso para servicios de emergencia)",
  emergxPdfQrHint:
    "Escanee el código QR para acceder de inmediato a los datos de emergencia",
  emergxPdfGeneratedOn: "Generado el: {date}",
  emergxPdfDisclaimer: "Esta tarjeta no sustituye al tratamiento médico.",
  emergxRevoked: "Revocado",
  emergxAuditTitle: "Registro de accesos",
  emergxAuditEmpty: "Aún no se han registrado accesos.",
  emergxAuditAccessType: "Tipo de acceso:",
  emergxAuditReason: "Motivo:",
  emergxAuditEmail: "Correo electrónico:",
  emergxAuditTokenExpired: "Token caducado:",
  emergxAuditRevokedAt: "Revocado:",
  emergxAuditConsent: "Consentimiento documentado:",
  emergxYes: "Sí",
  emergxAuditImpliedConsent: "Consentimiento implícito",
  emergxAuditResources: "Datos consultados:",
  emergxSevCritical: "Crítica",
  emergxSevHigh: "Alta",
  emergxSevMedium: "Media",
  emergxSevLow: "Baja",
  emergxValidating: "Validando…",
  emergxNoValidationData: "No hay datos de validación.",
  emergxValidationTitle: "Validación del perfil",
  emergxRevalidate: "Volver a validar",
  emergxCompleteness: "Integridad",
  emergxNoCriticalIssues: "No se han encontrado problemas críticos",
  emergxAllChecksPassed: "Todas las comprobaciones superadas",
  emergxFixIn: "Corregir en {route}",
  emergxLastValidated: "Última validación: {date}",
  emergxNo: "No",
  emergxDevPacemaker: "Marcapasos",
  emergxDevIcd: "Desfibrilador implantable (DAI)",
  emergxDevInsulinPump: "Bomba de insulina",
  emergxDevCochlear: "Implante coclear",
  emergxDevStent: "Stent",
  emergxDevJoint: "Prótesis articular",
  emergxDevHeartValve: "Válvula cardíaca",
  emergxDevNeurostimulator: "Neuroestimulador",
  emergxDevPort: "Port / catéter",
  emergxDevLoadFailed: "No se pudieron cargar sus dispositivos médicos.",
  emergxDevSaveFailed: "No se pudo guardar el dispositivo. Inténtelo de nuevo.",
  emergxDevRemoveFailed: "No se pudo eliminar el dispositivo.",
  emergxDevTitle: "Dispositivos médicos",
  emergxDevAdd: "Añadir dispositivo",
  emergxDevEmpty: "No hay dispositivos médicos registrados",
  emergxDevImplantedOn: "implantado el {date}",
  emergxDevMriSafe: "Compatible con RM",
  emergxDevMriUnsafe: "NO compatible con RM",
  emergxDevRemove: "Eliminar {type}",
  emergxDevType: "Tipo de dispositivo",
  emergxDevManufacturer: "Fabricante",
  emergxDevModel: "Modelo",
  emergxDevImplantDate: "Implantado el",
  emergxDevMriLabel: "Seguro en resonancia magnética",
  emergxDevNotRecorded: "No registrado",
  emergxDevNotes: "Notas",
  emergxDevNotesPlaceholder: "Lo que un profesional sanitario debe saber",
  emergxDevSave: "Guardar dispositivo",
  emergxCodeHigh: "Alta",
  emergxCodeLow: "Baja",
  emergxCodeUnableToAssess: "No evaluable",
  emergxCodeMild: "Leve",
  emergxCodeModerate: "Moderada",
  emergxCodeSevere: "Grave",
  emergxCodeActive: "Activa",
  emergxCodeInactive: "Inactiva",
  emergxCodeResolved: "Resuelta",
  emergxCodeRemission: "En remisión",
  emergxCodeRecurrence: "Recurrencia",
  emergxCodeRelapse: "Recaída",
  emergxConsentCheckboxAria:
    "Doy mi consentimiento para el acceso de emergencia a los datos",
  emergxAgeYears: "{n} años",
  emergxNotAvailable: "{title} no disponible",
  fhirxDashboardError: "Error del panel FHIR",
  fhirxLoadError: "Error al cargar los recursos FHIR",
  fhirxTryAgain: "Intentar de nuevo",
  fhirxDashboardTitle: "Panel FHIR",
  fhirxDashboardSubtitle: "Resumen de sus datos de salud en formato FHIR",
  fhirxTotalResources: "Total de recursos",
  fhirxAllResources: "Todos los recursos FHIR",
  fhirxObservations: "Observaciones",
  fhirxObservationsHint: "Mediciones médicas",
  fhirxAllergies: "Alergias",
  fhirxAllergiesHint: "Alergias conocidas",
  fhirxConditions: "Diagnósticos",
  fhirxConditionsHint: "Diagnósticos médicos",
  fhirxImmunizations: "Vacunas",
  fhirxImmunizationsHint: "Vacunas administradas",
  fhirxMedications: "Medicamentos",
  fhirxMedicationsHint: "Recetas",
  fhirxLoadingResources: "Cargando recursos FHIR…",
  fhirxNoResources: "No se encontraron recursos FHIR",
  fhirxNoResourcesHint:
    "Aún no hay datos de salud disponibles en formato FHIR.",
  fhirxExtTitle: "Recursos FHIR ampliados",
  fhirxExtSubtitle:
    "Panel para probar los tipos de recursos FHIR ampliados: DiagnosticReport, MedicationRequest, Encounter, Procedure, RelatedPerson, Flag",
  fhirxRefreshAll: "Actualizar todo",
  fhirxExportAll: "Exportar todos los recursos",
  fhirxExtLoading: "Cargando recursos FHIR ampliados…",
  fhirxExtLoaded: "{n} recursos FHIR ampliados cargados correctamente",
  fhirxExtAllResources: "Todos los recursos FHIR ampliados",
  fhirxDiagnosticReports: "Informes diagnósticos",
  fhirxDiagnosticReportsHint: "Informes de laboratorio y radiología",
  fhirxMedicationRequests: "Prescripciones de medicamentos",
  fhirxMedicationRequestsHint: "Medicamentos prescritos",
  fhirxEncounters: "Consultas",
  fhirxEncountersHint: "Visitas médicas",
  fhirxProcedures: "Procedimientos",
  fhirxProceduresHint: "Procedimientos médicos",
  fhirxRelatedPersons: "Personas relacionadas",
  fhirxRelatedPersonsHint: "Contactos de emergencia",
  fhirxHealthFlags: "Alertas de salud",
  fhirxFlagsHint: "Advertencias y alertas",
  fhirxTestStatus: "Estado de las pruebas",
  fhirxAllTypesImplemented: "Todos los tipos implementados",
  fhirxImplementationStatus: "Estado de la implementación",
  fhirxImplemented: "Implementado",
  fhirxExportOptions: "Opciones de exportación",
  fhirxExportIntro:
    "Exportar todos los recursos FHIR ampliados como un bundle completo, que incluye:",
  fhirxExportItemReports: "Informes diagnósticos con resultados de laboratorio",
  fhirxExportItemMeds: "Prescripciones con posología",
  fhirxExportItemEncounters: "Consultas y visitas médicas",
  fhirxExportItemProcedures: "Procedimientos realizados",
  fhirxExportItemRelated: "Personas relacionadas y contactos",
  fhirxExportItemFlags: "Alertas de salud y advertencias",
  fhirxDownloadBundle: "Descargar el bundle completo",
  fhirxDebugInfo: "Información de depuración",
  fhirxBundleTotals: "Totales del bundle:",
  fhirxAdditionalResources: "Recursos adicionales:",
  fhirxLastUpdated: "Última actualización: {date}",
  fhirxTypePatient: "Pacientes",
  fhirxTypeObservation: "Mediciones",
  fhirxTypeAllergyIntolerance: "Alergias",
  fhirxTypeCondition: "Diagnósticos",
  fhirxTypeImmunization: "Vacunas",
  fhirxTypeMedicationStatement: "Medicamentos",
  fhirxTypeDiagnosticReport: "Informes diagnósticos",
  fhirxTypeMedicationRequest: "Prescripciones",
  fhirxTypeEncounter: "Consultas",
  fhirxTypeProcedure: "Procedimientos",
  fhirxTypeDocumentReference: "Documentos",
  fhirxTypeMedia: "Multimedia",
  fhirxTypeRelatedPerson: "Personas de contacto",
  fhirxTypePractitioner: "Médicos",
  fhirxTypeCareTeam: "Equipo asistencial",
  fhirxTypeCoverage: "Seguro",
  fhirxTypeClaim: "Reclamaciones",
  fhirxTypeConsent: "Consentimientos",
  fhirxTypeFlag: "Alertas",
  fhirxTypeGoal: "Objetivos",
  fhirxTypeRiskAssessment: "Evaluaciones de riesgo",
  fhirxTypeServiceRequest: "Solicitudes de servicio",
  fhirxTypeAppointment: "Citas",
  fhirxTypeLocation: "Ubicaciones",
  fhirxTypeOrganization: "Organizaciones",
  fhirxTypeBundle: "Bundles",
  fhirxUnknownPatient: "Paciente desconocido",
  fhirxBornOn: "Nacido/a el {date}",
  fhirxActive: "Activo",
  fhirxInactive: "Inactivo",
  fhirxYes: "Sí",
  fhirxNo: "No",
  fhirxMeasurement: "Medición",
  fhirxAllergy: "Alergia",
  fhirxCatFood: "Alimento",
  fhirxCatMedication: "Medicamento",
  fhirxCatEnvironment: "Ambiental",
  fhirxCatBiologic: "Biológico",
  fhirxDiagnosis: "Diagnóstico",
  fhirxDoseNumber: "Dosis {n}",
  fhirxVaccination: "Vacuna",
  fhirxBundleGenerated: "Bundle FHIR generado correctamente",
  fhirxBundleFailed: "No se pudo generar el bundle FHIR",
  fhirxExported: "Datos FHIR exportados correctamente",
  fhirxExportFailed: "No se pudieron exportar los datos FHIR",
  fhirxInteropSubtitle: "Interoperabilidad HL7 FHIR R5 para datos de salud",
  fhirxExport: "Exportar",
  fhirxGenerateBundle: "Generar bundle",
  fhirxServiceStatus: "Estado del servicio FHIR",
  fhirxVersion: "Versión: {version}",
  fhirxSearchPlaceholder: "Buscar recursos…",
  fhirxAllTypes: "Todos los tipos",
  fhirxResourcesCount: "Recursos FHIR ({n})",
  fhirxAllData: "Todos sus datos de salud FHIR",
  fhirxFilteredBy: "Recursos FHIR filtrados por tipo: {type}",
  fhirxNoSearchMatch: "Ningún recurso coincide con su búsqueda",
  fhirxNoResourcesShort: "No se encontraron recursos",
  fhirxResourceDetails: "Detalles del recurso FHIR",
  fhirxCategory: "Categoría",
  fhirxSeverity: "Gravedad",
};

const pt: Strings = {
  cardxQrAlt: "Código QR",
  cardxBookingCodeRequired: "Introduza um código de reserva.",
  cardxQrGenerateFailed: "Não foi possível gerar o código QR. Tente novamente.",
  cardxCheckinQrTitle: "QR de check-in",
  cardxValidOneHourUntil: "Válido durante 1 hora (até às {time})",
  cardxCheckinScanHint:
    "Peça ao fornecedor de viagem que leia este código QR para iniciar o check-in.",
  cardxCheckinTitle: "Check-in",
  cardxCheckinSubtitle: "Gerar um código QR para o seu fornecedor de viagem",
  cardxBookingCode: "Código de reserva",
  cardxBookingCodePlaceholder: "p. ex. ABC123XY",
  cardxProviderName: "Nome do fornecedor",
  cardxOptionalParen: "(opcional)",
  cardxProviderPlaceholder: "p. ex. MSC Cruzeiros",
  cardxGenerateQr: "Gerar QR",
  cardxEmergencyQrFailed: "Não foi possível gerar o código QR de emergência.",
  cardxEmergencyQrGenerating: "A gerar QR de emergência…",
  cardxRetry: "Tentar novamente",
  cardxEmergencyQrTitle: "QR de emergência",
  cardxEmergencyQrSubtitle: "Os socorristas podem lê-lo para acesso imediato",
  cardxEmergencyNoLogin: "Os socorristas não precisam de iniciar sessão",
  cardxEmergencyAuditNote:
    "O nome, o e-mail e a função ficam registados. Pode consultar o histórico de acessos mais tarde.",
  cardxMedAccessFailed: "Não foi possível gerar o token MED ACCESS.",
  cardxMedAccessQrTitle: "QR MED ACCESS",
  cardxValidHoursUntil: "Válido {hours} h (até {time})",
  cardxDoctorMustBeRegistered: "O médico tem de estar registado no HEALTHPASS",
  cardxConsentNotificationNote:
    "Receberá um pedido de consentimento antes de quaisquer dados serem partilhados.",
  cardxMedAccessTitle: "Acesso médico",
  cardxMedAccessSubtitle:
    "Permitir que um médico aceda ao seu perfil de saúde completo",
  cardxAccessDuration: "Duração do acesso",
  cardxDurationHours: "{n} h",
  cardxDurationDays: "{n} d",
  cardxAccessExpiresNote:
    "O acesso expira automaticamente. Pode revogá-lo a qualquer momento.",
  cardxDoctorAccountNote:
    "O médico tem de ter uma conta HEALTHPASS. Confirmará o acesso através de uma notificação antes de quaisquer dados ficarem visíveis.",
  cardxActionCheckin: "Check-in",
  cardxActionCheckinHint: "QR para o fornecedor",
  cardxActionEmergency: "Emergência",
  cardxActionEmergencyHint: "Acesso para socorristas",
  cardxActionMedAccess: "Acesso médico",
  cardxActionMedAccessHint: "Partilhar com o seu médico",
  cardxActionWallet: "Carteira",
  cardxActionWalletHint: "Abrir a sua carteira HealthPass",
  cardxEntryOne: "{n} entrada",
  cardxEntryMany: "{n} entradas",
  cardxShortEmergency: "Emergência",
  cardxShortMedication: "Medicação",
  cardxShortVaccinations: "Vacinas",
  cardxShortCheckin: "Docs de check-in",
  cardxShortAppointments: "Consultas",
  cardxShortInsurance: "Seguro",
  cardxShortTravelDocs: "Docs de viagem",
  cardxShortLabResults: "Análises",
  cardxStatusAdd: "Adicionar",
  cardxStatusComplete: "Completo",
  cardxStatusPartial: "Parcial",
  cardxBackAria: "Cartão HealthPass, lado da carteira",
  cardxWalletHeading: "CARTEIRA",
  cardxPassesFilled: "{filled} de {total} passes preenchidos",
  cardxGenderNotStated: "sexo não indicado",
  cardxFrontAria: "Cartão HealthPass de {name}",
  cardxCardQrAlt: "Código QR do cartão",
  cardxQrLoading: "A carregar código QR",
  cardxCardNumber: "NÚMERO DO CARTÃO",
  cardxValidUntil: "VÁLIDO ATÉ",
  cardxStatusGranted: "Concedido",
  cardxStatusPending: "Pendente",
  cardxStatusDenied: "Recusado",
  cardxStatusRevoked: "Revogado",
  cardxSharedData: "Dados partilhados",
  cardxNoSharedData: "Ainda não há dados partilhados",
  cardxUnknownDoctor: "Médico desconhecido",
  cardxMedAccessLabel: "Acesso médico",
  cardxNoDataYet: "Ainda sem dados",
  cardxRevokeShareConfirm:
    "Revogar esta ligação de partilha? Qualquer pessoa com a ligação perderá o acesso de imediato.",
  cardxRevokeShareFailed: "Não foi possível revogar a ligação",
  cardxExpiredRevoked: "Expirados / revogados",
  cardxExpiresOn: "Expira a {date}",
  cardxCopyLink: "Copiar ligação",
  cardxOpenLink: "Abrir ligação",
  cardxCreateShareFailed: "Não foi possível criar a ligação de partilha",
  cardxSharePasswordPlaceholder: "Deixe em branco para não usar palavra-passe",
  cardxClaimNameEmailRequired: "Introduza o seu nome e o seu e-mail.",
  cardxClaimEmailInvalid: "Introduza um endereço de e-mail válido.",
  cardxClaimFailed: "Não foi possível enviar o pedido de acesso.",
  cardxMedRequestTitle: "Pedido de acesso médico",
  cardxMedRequestSubtitle: "Registo de saúde seguro HEALTHPASS",
  cardxVerifyingToken: "A verificar o token de acesso…",
  cardxMedInfoBanner:
    "Um doente partilhou consigo um código QR de acesso médico com duração limitada.",
  cardxRowValidUntil: "Válido até",
  cardxRowAccessLevel: "Nível de acesso",
  cardxRowAccessLevelValue: "Registo de saúde completo (apenas médicos)",
  cardxRowConsentRequired: "Consentimento necessário",
  cardxRowConsentRequiredValue: "Sim – o doente será notificado",
  cardxNeedAccount:
    "Precisa de uma conta HEALTHPASS para pedir acesso. Inicie sessão ou registe-se abaixo.",
  cardxLoginToContinue: "Iniciar sessão para continuar",
  cardxRequestAccess: "Pedir acesso",
  cardxClaimIntro:
    "Os seus dados serão enviados ao doente juntamente com o pedido de acesso.",
  cardxYourFullName: "O seu nome completo",
  cardxDoctorNamePlaceholder: "Dra. Ana Silva",
  cardxYourEmail: "O seu e-mail",
  cardxDoctorEmailPlaceholder: "ana.silva@hospital.pt",
  cardxSendAccessRequest: "Enviar pedido de acesso",
  cardxWaitingApproval: "A aguardar a aprovação do doente",
  cardxWaitingApprovalText:
    "O doente foi notificado e tem de aprovar o seu pedido. Esta página será atualizada automaticamente.",
  cardxCheckingEvery5s: "A verificar a cada 5 segundos…",
  cardxAccessGrantedBanner: "Acesso concedido. O doente aprovou o seu pedido.",
  cardxLoadingRecords: "A carregar registos de saúde…",
  cardxAccessDeniedTitle: "Acesso recusado",
  cardxAccessDeniedText: "O doente recusou este pedido de acesso.",
  cardxLinkExpiredTitle: "Ligação expirada",
  cardxLinkExpiredText:
    "Esta ligação de acesso expirou. Peça ao doente que gere uma nova.",
  cardxInvalidLinkTitle: "Ligação inválida",
  cardxInvalidLinkText:
    "Esta ligação de acesso não é válida ou já foi utilizada.",
  cardxMedFooter: "HEALTHPASS · Acesso médico seguro · travelhealthpass.app",
  cardxCritHigh: "Alta",
  cardxCritLow: "Baixa",
  cardxCritUnableToAssess: "Não avaliável",
  cardxBloodType: "Grupo sanguíneo",
  cardxAllergiesCount: "Alergias ({n})",
  cardxCriticalMedsCount: "Medicamentos críticos ({n})",
  cardxExtendedProfile: "Perfil de saúde alargado",
  cardxReqLoadFailed: "Não foi possível carregar os pedidos de acesso.",
  cardxReqUpdateFailed: "Não foi possível atualizar o pedido. Tente novamente.",
  cardxReqTitle: "Pedidos de acesso médico",
  cardxReqSubtitle: "Médicos que leram o seu código QR MED ACCESS",
  cardxReqEmpty: "Ainda não há pedidos de acesso médico.",
  cardxReqEmptyHint:
    "Quando um médico ler o seu código QR MED ACCESS, aparecerá aqui.",
  cardxReqSectionAwaitingDoctor: "Partilhado – a aguardar o médico",
  cardxReqSectionNeedsResponse: "Requer a sua resposta",
  cardxReqSectionResolved: "Resolvidos",
  cardxReqAwaitingYou: "A aguardar a sua resposta",
  cardxReqWaitingDoctor: "A aguardar o médico",
  cardxReqGranted: "Acesso concedido",
  cardxReqDoctorUnknown: "Médico ainda não identificado",
  cardxReqRequested: "Pedido: {date}",
  cardxReqExpires: "Expira: {date}",
  cardxReqAccessed: "Acedido: {date}",
  cardxReqGrant: "Conceder acesso",
  cardxReqDeny: "Recusar",
  cardxReqGrantedNote:
    "O médico tem acesso aos seus registos de saúde até o token expirar.",
  cardxPdfGenderMale: "M",
  cardxPdfGenderFemale: "F",
  cardxPdfWalletHeading: "CARTEIRA HEALTHPASS",
  emergxPdfCritical: "CRÍTICA",
  emergxPdfMild: "Ligeira",
  emergxPdfNotAssessed: "Não avaliada",
  emergxPdfTitle: "CARTÃO DE EMERGÊNCIA",
  emergxPdfPatientInfo: "Dados do doente",
  emergxPdfDateOfBirth: "Data de nascimento",
  emergxPdfAge: "Idade",
  emergxPdfGender: "Sexo",
  emergxPdfBloodType: "Grupo sanguíneo",
  emergxPdfHeightWeight: "Altura / peso",
  emergxPdfNationality: "Nacionalidade",
  emergxPdfAllergies: "Alergias e intolerâncias",
  emergxPdfNoAllergies: "Nenhuma alergia registada",
  emergxPdfMedications: "Medicação atual",
  emergxPdfNoMedications: "Nenhum medicamento registado",
  emergxPdfContacts: "Contactos de emergência",
  emergxPdfNoContacts: "Nenhum contacto indicado",
  emergxPdfQrTitle: "Código QR de emergência (acesso para socorristas)",
  emergxPdfQrHint:
    "Leia o código QR para aceder de imediato aos dados de emergência",
  emergxPdfGeneratedOn: "Gerado em: {date}",
  emergxPdfDisclaimer: "Este cartão não substitui o tratamento médico.",
  emergxRevoked: "Revogado",
  emergxAuditTitle: "Registo de acessos",
  emergxAuditEmpty: "Ainda não foram registados acessos.",
  emergxAuditAccessType: "Tipo de acesso:",
  emergxAuditReason: "Motivo:",
  emergxAuditEmail: "E-mail:",
  emergxAuditTokenExpired: "Token expirado:",
  emergxAuditRevokedAt: "Revogado:",
  emergxAuditConsent: "Consentimento registado:",
  emergxYes: "Sim",
  emergxAuditImpliedConsent: "Consentimento presumido",
  emergxAuditResources: "Dados acedidos:",
  emergxSevCritical: "Crítica",
  emergxSevHigh: "Alta",
  emergxSevMedium: "Média",
  emergxSevLow: "Baixa",
  emergxValidating: "A validar…",
  emergxNoValidationData: "Sem dados de validação.",
  emergxValidationTitle: "Validação do perfil",
  emergxRevalidate: "Validar novamente",
  emergxCompleteness: "Completude",
  emergxNoCriticalIssues: "Nenhum problema crítico encontrado",
  emergxAllChecksPassed: "Todas as verificações foram aprovadas",
  emergxFixIn: "Corrigir em {route}",
  emergxLastValidated: "Última validação: {date}",
  emergxNo: "Não",
  emergxDevPacemaker: "Pacemaker",
  emergxDevIcd: "Cardioversor-desfibrilhador implantável (CDI)",
  emergxDevInsulinPump: "Bomba de insulina",
  emergxDevCochlear: "Implante coclear",
  emergxDevStent: "Stent",
  emergxDevJoint: "Prótese articular",
  emergxDevHeartValve: "Válvula cardíaca",
  emergxDevNeurostimulator: "Neuroestimulador",
  emergxDevPort: "Cateter / port",
  emergxDevLoadFailed:
    "Não foi possível carregar os seus dispositivos médicos.",
  emergxDevSaveFailed:
    "Não foi possível guardar o dispositivo. Tente novamente.",
  emergxDevRemoveFailed: "Não foi possível remover o dispositivo.",
  emergxDevTitle: "Dispositivos médicos",
  emergxDevAdd: "Adicionar dispositivo",
  emergxDevEmpty: "Nenhum dispositivo médico registado",
  emergxDevImplantedOn: "implantado em {date}",
  emergxDevMriSafe: "Seguro para RM",
  emergxDevMriUnsafe: "NÃO seguro para RM",
  emergxDevRemove: "Remover {type}",
  emergxDevType: "Tipo de dispositivo",
  emergxDevManufacturer: "Fabricante",
  emergxDevModel: "Modelo",
  emergxDevImplantDate: "Implantado em",
  emergxDevMriLabel: "Seguro em ressonância magnética",
  emergxDevNotRecorded: "Não registado",
  emergxDevNotes: "Notas",
  emergxDevNotesPlaceholder: "O que um profissional de saúde deve saber",
  emergxDevSave: "Guardar dispositivo",
  emergxCodeHigh: "Alta",
  emergxCodeLow: "Baixa",
  emergxCodeUnableToAssess: "Não avaliável",
  emergxCodeMild: "Ligeira",
  emergxCodeModerate: "Moderada",
  emergxCodeSevere: "Grave",
  emergxCodeActive: "Ativa",
  emergxCodeInactive: "Inativa",
  emergxCodeResolved: "Resolvida",
  emergxCodeRemission: "Em remissão",
  emergxCodeRecurrence: "Recorrência",
  emergxCodeRelapse: "Recaída",
  emergxConsentCheckboxAria:
    "Dou o meu consentimento para o acesso de emergência aos dados",
  emergxAgeYears: "{n} anos",
  emergxNotAvailable: "{title} indisponível",
  fhirxDashboardError: "Erro no painel FHIR",
  fhirxLoadError: "Erro ao carregar os recursos FHIR",
  fhirxTryAgain: "Tentar novamente",
  fhirxDashboardTitle: "Painel FHIR",
  fhirxDashboardSubtitle: "Visão geral dos seus dados de saúde em formato FHIR",
  fhirxTotalResources: "Total de recursos",
  fhirxAllResources: "Todos os recursos FHIR",
  fhirxObservations: "Observações",
  fhirxObservationsHint: "Medições médicas",
  fhirxAllergies: "Alergias",
  fhirxAllergiesHint: "Alergias conhecidas",
  fhirxConditions: "Diagnósticos",
  fhirxConditionsHint: "Diagnósticos médicos",
  fhirxImmunizations: "Vacinas",
  fhirxImmunizationsHint: "Vacinas administradas",
  fhirxMedications: "Medicamentos",
  fhirxMedicationsHint: "Receitas",
  fhirxLoadingResources: "A carregar recursos FHIR…",
  fhirxNoResources: "Nenhum recurso FHIR encontrado",
  fhirxNoResourcesHint:
    "Ainda não há dados de saúde disponíveis em formato FHIR.",
  fhirxExtTitle: "Recursos FHIR alargados",
  fhirxExtSubtitle:
    "Painel para testar os tipos de recursos FHIR alargados: DiagnosticReport, MedicationRequest, Encounter, Procedure, RelatedPerson, Flag",
  fhirxRefreshAll: "Atualizar tudo",
  fhirxExportAll: "Exportar todos os recursos",
  fhirxExtLoading: "A carregar recursos FHIR alargados…",
  fhirxExtLoaded: "{n} recursos FHIR alargados carregados com sucesso",
  fhirxExtAllResources: "Todos os recursos FHIR alargados",
  fhirxDiagnosticReports: "Relatórios de diagnóstico",
  fhirxDiagnosticReportsHint: "Relatórios de laboratório e radiologia",
  fhirxMedicationRequests: "Prescrições de medicamentos",
  fhirxMedicationRequestsHint: "Medicamentos prescritos",
  fhirxEncounters: "Consultas",
  fhirxEncountersHint: "Visitas médicas",
  fhirxProcedures: "Procedimentos",
  fhirxProceduresHint: "Procedimentos médicos",
  fhirxRelatedPersons: "Pessoas relacionadas",
  fhirxRelatedPersonsHint: "Contactos de emergência",
  fhirxHealthFlags: "Alertas de saúde",
  fhirxFlagsHint: "Avisos e alertas",
  fhirxTestStatus: "Estado dos testes",
  fhirxAllTypesImplemented: "Todos os tipos implementados",
  fhirxImplementationStatus: "Estado da implementação",
  fhirxImplemented: "Implementado",
  fhirxExportOptions: "Opções de exportação",
  fhirxExportIntro:
    "Exportar todos os recursos FHIR alargados como um bundle completo, incluindo:",
  fhirxExportItemReports:
    "Relatórios de diagnóstico com resultados de análises",
  fhirxExportItemMeds: "Prescrições com posologia",
  fhirxExportItemEncounters: "Consultas e visitas médicas",
  fhirxExportItemProcedures: "Procedimentos realizados",
  fhirxExportItemRelated: "Pessoas relacionadas e contactos",
  fhirxExportItemFlags: "Alertas de saúde e avisos",
  fhirxDownloadBundle: "Transferir o bundle completo",
  fhirxDebugInfo: "Informações de depuração",
  fhirxBundleTotals: "Totais do bundle:",
  fhirxAdditionalResources: "Recursos adicionais:",
  fhirxLastUpdated: "Última atualização: {date}",
  fhirxTypePatient: "Doentes",
  fhirxTypeObservation: "Medições",
  fhirxTypeAllergyIntolerance: "Alergias",
  fhirxTypeCondition: "Diagnósticos",
  fhirxTypeImmunization: "Vacinas",
  fhirxTypeMedicationStatement: "Medicamentos",
  fhirxTypeDiagnosticReport: "Relatórios de diagnóstico",
  fhirxTypeMedicationRequest: "Prescrições",
  fhirxTypeEncounter: "Consultas",
  fhirxTypeProcedure: "Procedimentos",
  fhirxTypeDocumentReference: "Documentos",
  fhirxTypeMedia: "Multimédia",
  fhirxTypeRelatedPerson: "Pessoas de contacto",
  fhirxTypePractitioner: "Médicos",
  fhirxTypeCareTeam: "Equipa de cuidados",
  fhirxTypeCoverage: "Seguro",
  fhirxTypeClaim: "Pedidos de reembolso",
  fhirxTypeConsent: "Consentimentos",
  fhirxTypeFlag: "Alertas",
  fhirxTypeGoal: "Objetivos",
  fhirxTypeRiskAssessment: "Avaliações de risco",
  fhirxTypeServiceRequest: "Pedidos de serviço",
  fhirxTypeAppointment: "Consultas marcadas",
  fhirxTypeLocation: "Locais",
  fhirxTypeOrganization: "Organizações",
  fhirxTypeBundle: "Bundles",
  fhirxUnknownPatient: "Doente desconhecido",
  fhirxBornOn: "Nascido/a em {date}",
  fhirxActive: "Ativo",
  fhirxInactive: "Inativo",
  fhirxYes: "Sim",
  fhirxNo: "Não",
  fhirxMeasurement: "Medição",
  fhirxAllergy: "Alergia",
  fhirxCatFood: "Alimento",
  fhirxCatMedication: "Medicamento",
  fhirxCatEnvironment: "Ambiental",
  fhirxCatBiologic: "Biológico",
  fhirxDiagnosis: "Diagnóstico",
  fhirxDoseNumber: "Dose {n}",
  fhirxVaccination: "Vacina",
  fhirxBundleGenerated: "Bundle FHIR gerado com sucesso",
  fhirxBundleFailed: "Não foi possível gerar o bundle FHIR",
  fhirxExported: "Dados FHIR exportados com sucesso",
  fhirxExportFailed: "Não foi possível exportar os dados FHIR",
  fhirxInteropSubtitle: "Interoperabilidade HL7 FHIR R5 para dados de saúde",
  fhirxExport: "Exportar",
  fhirxGenerateBundle: "Gerar bundle",
  fhirxServiceStatus: "Estado do serviço FHIR",
  fhirxVersion: "Versão: {version}",
  fhirxSearchPlaceholder: "Pesquisar recursos…",
  fhirxAllTypes: "Todos os tipos",
  fhirxResourcesCount: "Recursos FHIR ({n})",
  fhirxAllData: "Todos os seus dados de saúde FHIR",
  fhirxFilteredBy: "Recursos FHIR filtrados por tipo: {type}",
  fhirxNoSearchMatch: "Nenhum recurso corresponde à sua pesquisa",
  fhirxNoResourcesShort: "Nenhum recurso encontrado",
  fhirxResourceDetails: "Detalhes do recurso FHIR",
  fhirxCategory: "Categoria",
  fhirxSeverity: "Gravidade",
};

export const cardEmergencyStrings: Record<Language, typeof en> = {
  en,
  de,
  fr,
  es,
  pt,
};
